/**
 * Brace1 Build Card scoring — isolated from dashboard chrome and Garage storage.
 *
 * Driver Level  = lifetime customer XP (all vehicles).
 * Build Class   = selected vehicle Build Score only. Never derived from Driver Level.
 *
 * Change numbers in BUILD_CARD.config only.
 */
(function (root) {
  "use strict";

  var SHARE_TOKEN_RE = /^[A-HJ-NP-Z2-9]{10}$/;
  var SHARE_CACHE_KEY = "brace1-public-build-url-v1";

  function isPublicShareUrl(url) {
    try {
      var parsed = new URL(String(url || ""), root.location.origin);
      var token = String(parsed.searchParams.get("share") || "").toUpperCase();
      return SHARE_TOKEN_RE.test(token) && /\/pages\/build\/?$/i.test(parsed.pathname);
    } catch (e) {
      return false;
    }
  }

  function shortPublicUrl(url) {
    try {
      var parsed = new URL(String(url || ""), root.location.origin);
      var token = String(parsed.searchParams.get("share") || "").toUpperCase();
      var host = String(parsed.host || "").replace(/^www\./, "");
      if (token) return host + " · " + token;
      return host + parsed.pathname.replace(/\/$/, "");
    } catch (e) {
      return String(url || "");
    }
  }

  function vehicleCacheKey(vehicle) {
    return [
      vehicle && vehicle.year ? String(vehicle.year) : "",
      vehicle && vehicle.make ? String(vehicle.make).trim().toLowerCase() : "",
      vehicle && vehicle.model ? String(vehicle.model).trim().toLowerCase() : ""
    ].join("|");
  }

  function readCachedShareUrl(vehicle) {
    if (!vehicle || !vehicle.make) return "";
    try {
      var all = JSON.parse(root.localStorage.getItem(SHARE_CACHE_KEY) || "{}");
      var url = all[vehicleCacheKey(vehicle)] || "";
      return isPublicShareUrl(url) ? url : "";
    } catch (e) {
      return "";
    }
  }

  function writeCachedShareUrl(vehicle, url) {
    if (!vehicle || !isPublicShareUrl(url)) return;
    try {
      var all = JSON.parse(root.localStorage.getItem(SHARE_CACHE_KEY) || "{}");
      all[vehicleCacheKey(vehicle)] = url;
      root.localStorage.setItem(SHARE_CACHE_KEY, JSON.stringify(all));
    } catch (e) {}
  }

  var BUILD_CARD = {
    config: {
      xp: {
        dollarsPerXp: 1,
        equipBonus: 50,
        levelBase: 500,
        levelStep: 100,
        milestones: [
          { count: 1, xp: 100 },
          { count: 3, xp: 150 },
          { count: 5, xp: 250 },
          { count: 10, xp: 500 },
          { count: 15, xp: 750 },
          { count: 20, xp: 1000 }
        ],
        categoryMilestones: [
          { count: 2, xp: 100 },
          { count: 3, xp: 200 },
          { count: 5, xp: 400 }
        ]
      },
      score: {
        verifiedPart: 10,
        equippedVerifiedPart: 10,
        ownerReportedFactor: 0.5,
        categoryCovered: 15,
        categoryDeep: 15,
        categoryDeepAt: 3,
        classes: [
          { min: 0, letter: "D", name: "STOCK / STARTER BUILD", shortName: "STOCK BUILD" },
          { min: 40, letter: "C", name: "MODIFIED", shortName: "MODIFIED" },
          { min: 90, letter: "B", name: "STREET PERFORMANCE", shortName: "STREET PERFORMANCE" },
          { min: 160, letter: "A", name: "ADVANCED PERFORMANCE", shortName: "ADVANCED PERFORMANCE" },
          { min: 250, letter: "S", name: "ELITE / TRACK BUILD", shortName: "ELITE / TRACK BUILD" }
        ]
      },
      driverTitles: [
        { min: 1, name: "ROOKIE" },
        { min: 6, name: "ENTHUSIAST" },
        { min: 11, name: "STREET TUNER" },
        { min: 21, name: "PERFORMANCE DRIVER" },
        { min: 31, name: "TRACK SPEC" },
        { min: 41, name: "ELITE BUILDER" }
      ],
      categoryLevelByEquipped: [0, 2, 4, 6, 8, 10],
      maxCategoryLevel: 10,
      /* owner is the only enabled mode. Later: private | link | public */
      visibility: "owner",
      rarity: {
        common: 1,
        uncommon: 2,
        rare: 3,
        epic: 4,
        legendary: 5
      },
      featuredLimit: 3,
      /*
        Achievement XP is session-calculated, once per id.
        Requirements never read Driver Level / Driver XP, so awarding XP cannot re-trigger badges.
        Persistence (earnedAt, seenIds) is not available yet.
      */
      achievements: [
        { id: "first_mod", name: "First Mod", description: "Every build starts somewhere.", type: "vehicle", icon: "wrench", rarity: "common", xpReward: 50, priority: 100, requirement: { kind: "vehicleVerified", count: 1 } },
        { id: "bolt_on_beginner", name: "Bolt-On Beginner", description: "Three upgrades deep.", type: "vehicle", icon: "wrench", rarity: "common", xpReward: 100, priority: 90, requirement: { kind: "vehicleVerified", count: 3 } },
        { id: "serious_build", name: "Serious Build", description: "This is no longer stock.", type: "vehicle", icon: "shield", rarity: "uncommon", xpReward: 150, priority: 80, requirement: { kind: "vehicleVerified", count: 5 } },
        { id: "double_digits", name: "Double Digits", description: "10 verified upgrades.", type: "vehicle", icon: "star", rarity: "rare", xpReward: 300, priority: 70, requirement: { kind: "vehicleVerified", count: 10 } },
        { id: "suspension_specialist", name: "Suspension Specialist", description: "Built to handle.", type: "vehicle", icon: "suspension", rarity: "uncommon", xpReward: 150, priority: 75, requirement: { kind: "vehicleEquippedCat", category: "suspension", count: 3 } },
        { id: "brake_specialist", name: "Brake Specialist", description: "Stopping power matters.", type: "vehicle", icon: "brake", rarity: "uncommon", xpReward: 150, priority: 75, requirement: { kind: "vehicleEquippedCat", category: "brakes", count: 3 } },
        { id: "engine_builder", name: "Engine Builder", description: "Power starts under the hood.", type: "vehicle", icon: "engine", rarity: "uncommon", xpReward: 150, priority: 75, requirement: { kind: "vehicleEquippedCat", category: "engine", count: 3 } },
        { id: "street_ready", name: "Street Ready", description: "Your build has officially evolved.", type: "vehicle", icon: "speed", rarity: "uncommon", xpReward: 200, priority: 65, requirement: { kind: "vehicleClass", letter: "C" } },
        { id: "street_performance", name: "Street Performance", description: "Built beyond basic.", type: "vehicle", icon: "speed", rarity: "rare", xpReward: 300, priority: 60, requirement: { kind: "vehicleClass", letter: "B" } },
        { id: "a_class", name: "A Class", description: "Advanced performance build.", type: "vehicle", icon: "trophy", rarity: "epic", xpReward: 500, priority: 50, requirement: { kind: "vehicleClass", letter: "A" } },
        { id: "s_class", name: "S Class", description: "Elite build status achieved.", type: "vehicle", icon: "crown", rarity: "legendary", xpReward: 1000, priority: 40, requirement: { kind: "vehicleClass", letter: "S" } },
        { id: "first_purchase", name: "First Purchase", description: "Your Brace1 journey begins.", type: "driver", icon: "star", rarity: "common", xpReward: 50, priority: 100, requirement: { kind: "driverVerified", count: 1 } },
        { id: "verified_5", name: "5 Verified Parts", description: "Five verified upgrades across your Garage.", type: "driver", icon: "wrench", rarity: "uncommon", xpReward: 100, priority: 90, requirement: { kind: "driverVerified", count: 5 } },
        { id: "verified_10", name: "10 Verified Parts", description: "Ten verified upgrades across your Garage.", type: "driver", icon: "shield", rarity: "rare", xpReward: 250, priority: 80, requirement: { kind: "driverVerified", count: 10 } },
        { id: "verified_20", name: "20 Verified Parts", description: "Twenty verified upgrades across your Garage.", type: "driver", icon: "trophy", rarity: "epic", xpReward: 500, priority: 70, requirement: { kind: "driverVerified", count: 20 } },
        { id: "multi_build_owner", name: "Multi-Build Owner", description: "One build wasn't enough.", type: "driver", icon: "garage", rarity: "rare", xpReward: 300, priority: 85, requirement: { kind: "driverVehicles", count: 2 } },
        { id: "b_class_club", name: "B Class Club", description: "At least one B Class vehicle in the Garage.", type: "driver", icon: "speed", rarity: "rare", xpReward: 250, priority: 55, requirement: { kind: "driverClass", letter: "B" } },
        { id: "a_class_club", name: "A Class Club", description: "At least one A Class vehicle in the Garage.", type: "driver", icon: "trophy", rarity: "epic", xpReward: 500, priority: 45, requirement: { kind: "driverClass", letter: "A" } },
        { id: "s_class_club", name: "S Class Club", description: "At least one S Class vehicle in the Garage.", type: "driver", icon: "crown", rarity: "legendary", xpReward: 1000, priority: 35, requirement: { kind: "driverClass", letter: "S" } }
      ],
      /*
        Vehicle-only build completion. Not Driver Level, not Build Class, not spend.
        Change numbers here only. Owner-reported equipped parts use ownerReportedFactor
        when that data exists; do not invent owner-reported parts.
      */
      completion: {
        categoryPartMap: {
          0: 0,
          1: 25,
          2: 50,
          3: 75,
          4: 100
        },
        ownerReportedFactor: 0.5,
        labels: [
          { min: 0, name: "STARTING BUILD" },
          { min: 20, name: "EARLY BUILD" },
          { min: 40, name: "DEVELOPING BUILD" },
          { min: 60, name: "ADVANCED BUILD" },
          { min: 80, name: "NEAR COMPLETE" },
          { min: 100, name: "FULL BUILD" }
        ],
        recommendOrder: ["brakes", "suspension", "wheels", "engine", "exhaust", "aero", "interior", "audio"],
        completeDisplay: "COMPLETE",
        garageSlotParam: "gf_type",
        garageCategoryParam: "",
        shopLabels: {
          engine: "ENGINE",
          suspension: "SUSPENSION",
          brakes: "BRAKE",
          exhaust: "EXHAUST",
          wheels: "WHEEL",
          aero: "AERO",
          interior: "INTERIOR",
          audio: "AUDIO"
        },
        copy: {
          developed: "Your build is {pct}% developed",
          productHint: "Improve {category} progression from {from}% → {to}%",
          categoryHint: "Your {category} setup has room to grow.",
          emptyHint: "Start your build with a compatible {category} upgrade.",
          fullHint: "All tracked build categories complete.",
          shopProduct: "Shop this upgrade",
          shopCategory: "Shop {shop} upgrades",
          explore: "Explore new parts",
          viewBuild: "View your build",
          fullKicker: "FULL BUILD",
          nextKicker: "Next recommended upgrade"
        }
      }
    },

    CATEGORIES: [
      { id: "engine", label: "ENGINE", slots: [], keywords: ["engine", "intake", "turbo", "supercharger", "pulley"] },
      { id: "suspension", label: "SUSPENSION", slots: ["strut"], keywords: ["strut", "brace", "sway", "coilover", "control arm", "chassis"] },
      { id: "brakes", label: "BRAKES", slots: [], keywords: ["brake", "rotor", "caliper", "pad"] },
      { id: "exhaust", label: "EXHAUST", slots: [], keywords: ["exhaust", "catback", "header", "muffler"] },
      { id: "wheels", label: "WHEELS & TIRES", slots: [], keywords: ["wheel", "tire", "rim"] },
      { id: "aero", label: "AERO & EXTERIOR", slots: ["mirrors"], keywords: ["mirror", "spoiler", "lip", "wing", "aero", "splitter", "diffuser"] },
      { id: "interior", label: "INTERIOR", slots: ["shifter", "paddles"], keywords: ["shifter", "paddle", "seat", "steering", "interior"] },
      { id: "audio", label: "AUDIO", slots: [], keywords: ["audio", "speaker", "subwoofer", "amp"] }
    ],

    merchRe: /shipping|tax|hoodie|gift card|replacement screws|sticker|apparel|sweatshirt|\bhat\b|\btee\b|\bshirt\b/i,

    partKey: function (part) {
      return String((part && (part.id || part.handle)) || "");
    },

    isVerified: function (part) {
      if (!part) return false;
      if (part.ownerReported === true || part.source === "owner") return false;
      if (part.verified === false) return false;
      return Boolean(part.purchased || part.verified === true || Number(part.paidCents) > 0);
    },

    isOwnerReported: function (part) {
      return Boolean(part && (part.ownerReported === true || part.source === "owner"));
    },

    isEquipped: function (part, equippedKeys) {
      var keys = equippedKeys || [];
      var id = String((part && part.id) || "");
      var handle = String((part && part.handle) || "");
      return (id && keys.indexOf(id) !== -1) || (handle && keys.indexOf(handle) !== -1);
    },

    spend: function (part) {
      if (!part || this.merchRe.test(String(part.name || part.title || ""))) return 0;
      var paid = Number(part.paidCents);
      if (Number.isFinite(paid) && paid > 0) return paid / 100;
      return Number(part.price || 0) || 0;
    },

    categorize: function (part) {
      var slot = String((part && part.slot) || "").toLowerCase();
      var hay = [part && part.category, part && part.name, part && part.title, part && part.meta]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      var i;
      for (i = 0; i < this.CATEGORIES.length; i++) {
        if (slot && this.CATEGORIES[i].slots.indexOf(slot) !== -1) return this.CATEGORIES[i].id;
      }
      for (i = 0; i < this.CATEGORIES.length; i++) {
        var keys = this.CATEGORIES[i].keywords;
        var k;
        for (k = 0; k < keys.length; k++) {
          if (hay.indexOf(keys[k]) !== -1) return this.CATEGORIES[i].id;
        }
      }
      return "";
    },

    categoryLabel: function (id) {
      var i;
      for (i = 0; i < this.CATEGORIES.length; i++) {
        if (this.CATEGORIES[i].id === id) return this.CATEGORIES[i].label;
      }
      return "PART";
    },

    pickBand: function (rows, value) {
      var found = rows[0];
      var i;
      for (i = 0; i < rows.length; i++) {
        if (value >= rows[i].min) found = rows[i];
      }
      return found;
    },

    xpRequiredForLevel: function (level) {
      var n = Math.max(1, Number(level) || 1);
      var base = this.config.xp.levelBase;
      var step = this.config.xp.levelStep;
      return base + (n - 1) * step;
    },

    driverProgress: function (totalXp) {
      var xp = Math.max(0, Math.round(Number(totalXp) || 0));
      var level = 1;
      var remaining = xp;
      var need = this.xpRequiredForLevel(level);
      while (remaining >= need && level < 99) {
        remaining -= need;
        level += 1;
        need = this.xpRequiredForLevel(level);
      }
      var title = this.pickBand(this.config.driverTitles, level);
      return {
        xp: xp,
        level: level,
        title: title.name,
        into: remaining,
        need: need,
        label: remaining.toLocaleString("en-US") + " / " + need.toLocaleString("en-US") + " XP",
        pct: need ? Math.max(0, Math.min(100, (remaining / need) * 100)) : 0
      };
    },

    categoryLevel: function (equippedCount) {
      var map = this.config.categoryLevelByEquipped || [0];
      var n = Math.max(0, Number(equippedCount) || 0);
      var idx = Math.min(n, map.length - 1);
      return Math.max(0, Math.min(this.config.maxCategoryLevel, map[idx]));
    },

    categoryShortLabel: function (id) {
      var short = {
        engine: "ENGINE",
        suspension: "SUSPENSION",
        brakes: "BRAKES",
        exhaust: "EXHAUST",
        wheels: "WHEELS",
        aero: "AERO",
        interior: "INTERIOR",
        audio: "AUDIO"
      };
      return short[id] || this.categoryLabel(id);
    },

    partCountsTowardCategory: function (part, parts, equippedKeys) {
      var self = this;
      if (!part) return false;
      return (parts || []).some(function (owned) {
        return (
          self.sameCatalogPart(part, owned) &&
          self.isVerified(owned) &&
          self.isEquipped(owned, equippedKeys)
        );
      });
    },

    partTypeKey: function (part) {
      if (!part) return "";
      var slot = String(part.slot || "").toLowerCase().trim();
      if (slot) return "slot:" + slot;
      var cat = this.categorize(part);
      if (!cat) return "";
      var hay = [part.category, part.name, part.title, part.meta, part.type]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      var i;
      var k;
      for (i = 0; i < this.CATEGORIES.length; i++) {
        if (this.CATEGORIES[i].id !== cat) continue;
        var keys = this.CATEGORIES[i].keywords || [];
        for (k = 0; k < keys.length; k++) {
          if (hay.indexOf(keys[k]) !== -1) return cat + ":" + keys[k];
        }
      }
      return "cat:" + cat;
    },

    typeAlreadyCounts: function (part, parts, equippedKeys) {
      var typeKey = this.partTypeKey(part);
      var self = this;
      if (!typeKey) return this.partCountsTowardCategory(part, parts, equippedKeys);
      return (parts || []).some(function (owned) {
        return (
          self.partTypeKey(owned) === typeKey &&
          self.isVerified(owned) &&
          self.isEquipped(owned, equippedKeys)
        );
      });
    },

    previewGhostPart: function (part) {
      if (!part) return null;
      var ghost = {};
      var key;
      for (key in part) {
        if (Object.prototype.hasOwnProperty.call(part, key)) ghost[key] = part[key];
      }
      ghost.verified = true;
      ghost.purchased = true;
      ghost.ownerReported = false;
      if (ghost.source === "owner") ghost.source = "catalog";
      if (!(Number(ghost.paidCents) > 0)) {
        ghost.paidCents = Math.max(1, Math.round((Number(ghost.price) || 0) * 100));
      }
      return ghost;
    },

    mergePreviewPart: function (parts, ghost, source) {
      var found = false;
      var out = (parts || []).map(function (row) {
        if (BUILD_CARD.sameCatalogPart(row, source)) {
          found = true;
          return ghost;
        }
        return row;
      });
      if (!found) out = out.concat([ghost]);
      return out;
    },

    /*
      Live shopping preview only. Does not change categoryLevelByEquipped,
      vehicleScore, or persisted Garage data. Preview is never published.
    */
    tuningPreview: function (part, input) {
      return this.tuningPreviewMany(part ? [part] : [], input);
    },

    tuningPreviewMany: function (parts, input) {
      input = input || {};
      var committed = this.compute(input);
      var list = parts || [];
      var vehicle = input.vehicle && input.vehicle.make ? input.vehicle : null;
      var vehicleParts = this.ownedParts(input.vehicleParts || []);
      var equippedKeys = input.equippedKeys || [];
      var counts = this.tallyCategories(vehicleParts, equippedKeys);
      var maxLevel = this.config.maxCategoryLevel || 10;
      var addByCat = {};
      var ghosts = [];
      var seen = {};
      var seenType = {};
      var classified = 0;
      var duplicateTypeSkipped = 0;
      var i;
      for (i = 0; i < list.length; i++) {
        var part = list[i];
        if (!part) continue;
        var key = this.partKey(part);
        if (key && seen[key]) continue;
        if (key) seen[key] = true;
        var catId = this.categorize(part);
        if (!catId) continue;
        classified += 1;
        if (vehicle && (part.vehicles || []).length && !this.fitsVehicle(part, vehicle)) continue;
        if (this.partCountsTowardCategory(part, vehicleParts, equippedKeys)) continue;
        if (this.typeAlreadyCounts(part, vehicleParts, equippedKeys)) {
          duplicateTypeSkipped += 1;
          continue;
        }
        var typeKey = this.partTypeKey(part);
        if (typeKey && seenType[typeKey]) {
          duplicateTypeSkipped += 1;
          continue;
        }
        if (typeKey) seenType[typeKey] = true;
        addByCat[catId] = (addByCat[catId] || 0) + 1;
        ghosts.push(part);
      }
      var effects = [];
      for (i = 0; i < this.CATEGORIES.length; i++) {
        var row = this.CATEGORIES[i];
        var currentCount = (counts[row.id] && counts[row.id].equipped) || 0;
        var currentLevel = this.categoryLevel(currentCount);
        var maxed = currentLevel >= maxLevel;
        var add = maxed ? 0 : addByCat[row.id] || 0;
        var previewCount = currentCount + add;
        var previewLevel = this.categoryLevel(previewCount);
        var delta = previewLevel - currentLevel;
        if (!add && !(maxed && addByCat[row.id])) continue;
        if (delta <= 0 && !maxed) continue;
        effects.push({
          categoryId: row.id,
          categoryLabel: this.categoryShortLabel(row.id),
          currentLevel: currentLevel,
          previewLevel: previewLevel,
          currentCount: currentCount,
          previewCount: previewCount,
          delta: delta,
          maxed: maxed,
          added: add
        });
      }
      var previewModel = committed;
      if (ghosts.length) {
        var nextKeys = equippedKeys.slice();
        var nextVehicle = input.vehicleParts || [];
        var nextAll = input.allParts || [];
        for (i = 0; i < ghosts.length; i++) {
          var ghost = this.previewGhostPart(ghosts[i]);
          var gkey = this.partKey(ghost);
          if (gkey && nextKeys.indexOf(gkey) === -1) nextKeys.push(gkey);
          nextVehicle = this.mergePreviewPart(nextVehicle, ghost, ghosts[i]);
          nextAll = this.mergePreviewPart(nextAll, ghost, ghosts[i]);
        }
        previewModel = this.compute({
          allParts: nextAll,
          vehicleParts: nextVehicle,
          vehicle: input.vehicle,
          equippedKeys: nextKeys,
          groups: input.groups,
          driverName: input.driverName,
          memberYear: input.memberYear,
          customerId: input.customerId,
          garagePath: input.garagePath
        });
      }
      var primary = effects[0] || null;
      return {
        unclassified: list.length > 0 && classified === 0,
        effects: effects,
        categoryId: primary ? primary.categoryId : "",
        categoryLabel: primary ? primary.categoryLabel : "",
        currentLevel: primary ? primary.currentLevel : 0,
        previewLevel: primary ? primary.previewLevel : 0,
        currentCount: primary ? primary.currentCount : 0,
        previewCount: primary ? primary.previewCount : 0,
        delta: primary ? primary.delta : 0,
        alreadyCounts: ghosts.length === 0 && classified > 0,
        duplicateTypeSkipped: duplicateTypeSkipped,
        maxed: primary ? primary.maxed : false,
        currentScore: committed.score,
        previewScore: previewModel.score,
        currentClass: committed.classLetter,
        previewClass: previewModel.classLetter,
        classChanged: committed.classLetter !== previewModel.classLetter,
        scoreExact: true,
        committed: committed
      };
    },

    money: function (n) {
      return "$" + Math.round(Number(n) || 0).toLocaleString("en-US");
    },

    displayId: function (customerId, vehicle) {
      var make = String((vehicle && vehicle.make) || "");
      var model = String((vehicle && vehicle.model) || "");
      var seed = String(customerId || "0") + "|" + make + "|" + model;
      var hash = 5381;
      var i;
      for (i = 0; i < seed.length; i++) hash = (hash * 33) ^ seed.charCodeAt(i);
      var n = Math.abs(hash) % 10000;
      var slug = (model || make || "BUILD").replace(/[^A-Za-z0-9]/g, "").slice(0, 8).toUpperCase() || "BUILD";
      return "B1-" + slug + "-" + String(n).padStart(4, "0");
    },

    fitsVehicle: function (part, vehicle) {
      if (!vehicle || !vehicle.make || !vehicle.model) return false;
      var y = Number(vehicle.year);
      return (part.vehicles || []).some(function (v) {
        return v.make === vehicle.make && v.model === vehicle.model && (!y || (y >= v.yearStart && y <= v.yearEnd));
      });
    },

    emptyCatCounts: function () {
      var counts = {};
      var i;
      for (i = 0; i < this.CATEGORIES.length; i++) {
        counts[this.CATEGORIES[i].id] = { verified: 0, equipped: 0, equippedOwnerReported: 0 };
      }
      return counts;
    },

    tallyCategories: function (parts, equippedKeys) {
      var counts = this.emptyCatCounts();
      var i;
      for (i = 0; i < (parts || []).length; i++) {
        var part = parts[i];
        var cat = this.categorize(part);
        if (!cat || !counts[cat]) continue;
        if (this.isVerified(part)) counts[cat].verified += 1;
        if (this.isEquipped(part, equippedKeys) && this.isVerified(part)) counts[cat].equipped += 1;
        if (this.isEquipped(part, equippedKeys) && this.isOwnerReported(part)) {
          counts[cat].equippedOwnerReported += 1;
        }
      }
      return counts;
    },

    completionInput: function (vehicle) {
      if (!vehicle) {
        return { vehicle: null, parts: [], equippedKeys: [], catalog: null, garagePath: "/pages/garage" };
      }
      if (Array.isArray(vehicle)) {
        return { vehicle: null, parts: vehicle, equippedKeys: [], catalog: null, garagePath: "/pages/garage" };
      }
      var parts = vehicle.parts || vehicle.vehicleParts || [];
      var equippedKeys = vehicle.equippedKeys || [];
      var car = vehicle.vehicle && vehicle.vehicle.make ? vehicle.vehicle : vehicle;
      if (car && !(car.make && car.model) && vehicle.make && vehicle.model) {
        car = { year: vehicle.year, make: vehicle.make, model: vehicle.model };
      }
      if (car && !(car.make && car.model)) car = null;
      return {
        vehicle: car,
        parts: parts,
        equippedKeys: equippedKeys,
        catalog: vehicle.catalog || null,
        garagePath: vehicle.garagePath || "/pages/garage"
      };
    },

    completionMapKeys: function () {
      var map = (this.config.completion && this.config.completion.categoryPartMap) || { 0: 0, 4: 100 };
      return Object.keys(map)
        .map(function (k) {
          return Number(k);
        })
        .filter(function (k) {
          return Number.isFinite(k);
        })
        .sort(function (a, b) {
          return a - b;
        });
    },

    percentFromWeight: function (weight) {
      var map = (this.config.completion && this.config.completion.categoryPartMap) || { 0: 0, 4: 100 };
      var n = Math.max(0, Number(weight) || 0);
      var keys = this.completionMapKeys();
      if (!keys.length) return 0;
      if (n >= keys[keys.length - 1]) return Number(map[keys[keys.length - 1]]);
      var lo = keys[0];
      var hi = keys[keys.length - 1];
      var i;
      for (i = 0; i < keys.length; i++) {
        if (keys[i] <= n) lo = keys[i];
        if (keys[i] >= n) {
          hi = keys[i];
          break;
        }
      }
      if (lo === hi) return Number(map[lo]);
      var plo = Number(map[lo]);
      var phi = Number(map[hi]);
      return plo + (phi - plo) * ((n - lo) / (hi - lo));
    },

    nextMappedPercent: function (percent) {
      var map = (this.config.completion && this.config.completion.categoryPartMap) || { 0: 0, 4: 100 };
      var keys = this.completionMapKeys();
      var value = Number(percent) || 0;
      var i;
      for (i = 0; i < keys.length; i++) {
        var mapped = Number(map[keys[i]]);
        if (mapped > value) return mapped;
      }
      return 100;
    },

    categoryEquippedWeight: function (row) {
      var factor = (this.config.completion && this.config.completion.ownerReportedFactor) || 0;
      var verified = (row && row.equipped) || 0;
      var owner = (row && row.equippedOwnerReported) || 0;
      return verified + owner * factor;
    },

    completionLabel: function (percent) {
      var labels = (this.config.completion && this.config.completion.labels) || [];
      var found = labels[0] || { name: "STARTING BUILD" };
      var i;
      for (i = 0; i < labels.length; i++) {
        if (percent >= labels[i].min) found = labels[i];
      }
      return found.name;
    },

    recommendRank: function (id) {
      var order = (this.config.completion && this.config.completion.recommendOrder) || [];
      var idx = order.indexOf(id);
      return idx === -1 ? 99 : idx;
    },

    shopLabel: function (id) {
      var labels = (this.config.completion && this.config.completion.shopLabels) || {};
      return labels[id] || this.categoryLabel(id);
    },

    formatCopy: function (template, vars) {
      var out = String(template || "");
      var key;
      vars = vars || {};
      for (key in vars) {
        if (!Object.prototype.hasOwnProperty.call(vars, key)) continue;
        out = out.split("{" + key + "}").join(String(vars[key]));
      }
      return out;
    },

    loadedCatalog: function () {
      var api = root.BRACE1_GARAGE_API;
      if (api && typeof api.catalogParts === "function") return api.catalogParts() || [];
      return [];
    },

    catalogProductUrl: function (part) {
      return String((part && (part.url || part.productUrl)) || "").trim();
    },

    isCatalogAvailable: function (part) {
      if (!part) return false;
      if (part.available === false || part.available === "false") return false;
      if (part.published === false) return false;
      return true;
    },

    isValidProductUrl: function (url) {
      var s = String(url || "").trim();
      if (!s || s === "#" || /^\s*javascript:/i.test(s)) return false;
      return true;
    },

    sameCatalogPart: function (a, b) {
      if (!a || !b) return false;
      var aid = String(a.id || "");
      var bid = String(b.id || "");
      var aHandle = String(a.handle || "");
      var bHandle = String(b.handle || "");
      var aVar = String(a.variantId || "");
      var bVar = String(b.variantId || "");
      return (aid && bid && aid === bid) || (aHandle && bHandle && aHandle === bHandle) || (aVar && bVar && aVar === bVar);
    },

    isOwnedOnVehicle: function (part, vehicleParts) {
      var self = this;
      return (vehicleParts || []).some(function (owned) {
        return self.sameCatalogPart(part, owned);
      });
    },

    compatibleCatalogParts: function (catalog, vehicle, categoryId) {
      var self = this;
      if (!vehicle || !vehicle.make || !vehicle.model) return [];
      return (catalog || []).filter(function (part) {
        if (!self.isCatalogAvailable(part)) return false;
        if (!self.isValidProductUrl(self.catalogProductUrl(part))) return false;
        if (!self.fitsVehicle(part, vehicle)) return false;
        return self.categorize(part) === categoryId;
      });
    },

    recommendableCatalogParts: function (catalog, vehicle, categoryId, vehicleParts, equippedKeys) {
      var self = this;
      return this.compatibleCatalogParts(catalog, vehicle, categoryId).filter(function (part) {
        if (self.isOwnedOnVehicle(part, vehicleParts)) return false;
        if (self.isEquipped(part, equippedKeys)) return false;
        return true;
      });
    },

    garageUrlFor: function (vehicle, categoryId, garagePath) {
      var path = garagePath || "/pages/garage";
      var params = [];
      function add(key, value) {
        if (value === undefined || value === null || value === "") return;
        params.push(encodeURIComponent(key) + "=" + encodeURIComponent(String(value)));
      }
      if (vehicle && vehicle.year) add("year", vehicle.year);
      if (vehicle && vehicle.make) add("make", vehicle.make);
      if (vehicle && vehicle.model) add("model", vehicle.model);
      var cfg = this.config.completion || {};
      var cat = null;
      var i;
      for (i = 0; i < this.CATEGORIES.length; i++) {
        if (this.CATEGORIES[i].id === categoryId) cat = this.CATEGORIES[i];
      }
      if (cfg.garageSlotParam && cat && cat.slots && cat.slots.length) {
        add(cfg.garageSlotParam, cat.slots.join(","));
      }
      if (cfg.garageCategoryParam && categoryId) {
        add(cfg.garageCategoryParam, categoryId);
      }
      return params.length ? path + "?" + params.join("&") : path;
    },

    getCategoryCompletion: function (vehicle) {
      var input = this.completionInput(vehicle);
      var counts = this.tallyCategories(this.ownedParts(input.parts), input.equippedKeys);
      var self = this;
      return this.CATEGORIES.map(function (row) {
        var weight = self.categoryEquippedWeight(counts[row.id]);
        var raw = self.percentFromWeight(weight);
        var percent = Math.round(raw);
        if (percent < 0) percent = 0;
        if (percent > 100) percent = 100;
        return {
          id: row.id,
          label: row.label,
          equipped: (counts[row.id] && counts[row.id].equipped) || 0,
          equippedOwnerReported: (counts[row.id] && counts[row.id].equippedOwnerReported) || 0,
          weight: weight,
          percent: percent,
          complete: percent >= 100
        };
      });
    },

    getBuildCompletion: function (vehicle) {
      var categories = this.getCategoryCompletion(vehicle);
      var sum = 0;
      var i;
      for (i = 0; i < categories.length; i++) sum += categories[i].percent;
      var percent = categories.length ? Math.round(sum / categories.length) : 0;
      if (percent < 0) percent = 0;
      if (percent > 100) percent = 100;
      return {
        percent: percent,
        label: this.completionLabel(percent),
        categories: categories,
        complete: percent >= 100,
        empty: percent === 0
      };
    },

    getLowestCompletionCategories: function (vehicle) {
      var categories = this.getCategoryCompletion(vehicle);
      var lowest = 100;
      var i;
      for (i = 0; i < categories.length; i++) {
        if (categories[i].percent < lowest) lowest = categories[i].percent;
      }
      var self = this;
      return categories
        .filter(function (row) {
          return row.percent === lowest;
        })
        .sort(function (a, b) {
          return self.recommendRank(a.id) - self.recommendRank(b.id);
        });
    },

    getNextUpgradeRecommendation: function (vehicle) {
      var input = this.completionInput(vehicle);
      var completion = this.getBuildCompletion(input);
      var copy = (this.config.completion && this.config.completion.copy) || {};
      var catalog = input.catalog || this.loadedCatalog();
      var garagePath = input.garagePath || "/pages/garage";
      var exploreHref = this.garageUrlFor(input.vehicle, "", garagePath);
      if (completion.complete) {
        return {
          mode: "complete",
          categoryId: "",
          categoryLabel: "",
          product: null,
          kicker: copy.fullKicker || "FULL BUILD",
          name: copy.fullKicker || "FULL BUILD",
          hint: copy.fullHint || "All tracked build categories complete.",
          ctaLabel: copy.explore || "Explore new parts",
          ctaHref: exploreHref,
          secondaryLabel: copy.viewBuild || "View your build",
          secondaryHref: "#upgrades"
        };
      }
      var self = this;
      var incomplete = completion.categories
        .filter(function (row) {
          return row.percent < 100;
        })
        .slice()
        .sort(function (a, b) {
          if (a.percent !== b.percent) return a.percent - b.percent;
          return self.recommendRank(a.id) - self.recommendRank(b.id);
        });
      var empty = completion.empty;
      var i;
      var fallback = null;
      for (i = 0; i < incomplete.length; i++) {
        var cat = incomplete[i];
        var compatible = this.compatibleCatalogParts(catalog, input.vehicle, cat.id);
        var recs = this.recommendableCatalogParts(catalog, input.vehicle, cat.id, input.parts, input.equippedKeys);
        var shop = this.shopLabel(cat.id);
        var categoryHref = this.garageUrlFor(input.vehicle, cat.id, garagePath);
        var vars = {
          category: cat.label.toLowerCase(),
          shop: shop,
          pct: String(completion.percent),
          from: String(cat.percent),
          to: String(this.nextMappedPercent(cat.percent))
        };
        var categoryRec = {
          mode: "category",
          categoryId: cat.id,
          categoryLabel: cat.label,
          product: null,
          kicker: copy.nextKicker || "Next recommended upgrade",
          name: "",
          hint: empty
            ? this.formatCopy(copy.emptyHint || "Start your build with a compatible {category} upgrade.", vars)
            : this.formatCopy(copy.categoryHint || "Your {category} setup has room to grow.", vars),
          ctaLabel: this.formatCopy(copy.shopCategory || "Shop {shop} upgrades", vars),
          ctaHref: categoryHref
        };
        if (!fallback && compatible.length) fallback = categoryRec;
        if (!empty && recs[0]) {
          var part = recs[0];
          return {
            mode: "product",
            categoryId: cat.id,
            categoryLabel: cat.label,
            product: {
              id: part.id || "",
              handle: part.handle || "",
              name: part.name || part.title || "Upgrade",
              url: this.catalogProductUrl(part),
              price: part.price
            },
            kicker: copy.nextKicker || "Next recommended upgrade",
            name: part.name || part.title || "Upgrade",
            hint: this.formatCopy(copy.productHint || "Improve {category} progression from {from}% → {to}%", vars),
            ctaLabel: copy.shopProduct || "Shop this upgrade",
            ctaHref: this.catalogProductUrl(part)
          };
        }
        if (empty && compatible.length) return categoryRec;
      }
      if (fallback) return fallback;
      var first = incomplete[0] || completion.categories[0];
      if (!first) {
        return {
          mode: "category",
          categoryId: "",
          categoryLabel: "",
          product: null,
          kicker: copy.nextKicker || "Next recommended upgrade",
          name: "",
          hint: copy.emptyHint || "Start your build with a compatible upgrade.",
          ctaLabel: copy.explore || "Explore new parts",
          ctaHref: exploreHref
        };
      }
      return {
        mode: "category",
        categoryId: first.id,
        categoryLabel: first.label,
        product: null,
        kicker: copy.nextKicker || "Next recommended upgrade",
        name: "",
        hint: empty
          ? this.formatCopy(copy.emptyHint || "Start your build with a compatible {category} upgrade.", {
              category: first.label.toLowerCase(),
              shop: this.shopLabel(first.id)
            })
          : this.formatCopy(copy.categoryHint || "Your {category} setup has room to grow.", {
              category: first.label.toLowerCase(),
              shop: this.shopLabel(first.id)
            }),
        ctaLabel: this.formatCopy(copy.shopCategory || "Shop {shop} upgrades", { shop: this.shopLabel(first.id) }),
        ctaHref: this.garageUrlFor(input.vehicle, first.id, garagePath)
      };
    },

    categoryMilestoneXp: function (parts, equippedKeys) {
      var counts = this.tallyCategories(parts, equippedKeys);
      var xp = 0;
      var id;
      for (id in counts) {
        if (!Object.prototype.hasOwnProperty.call(counts, id)) continue;
        var n = counts[id].verified;
        this.config.xp.categoryMilestones.forEach(function (row) {
          if (n >= row.count) xp += row.xp;
        });
      }
      return xp;
    },

    purchaseMilestoneXp: function (verifiedCount) {
      var xp = 0;
      this.config.xp.milestones.forEach(function (row) {
        if (verifiedCount >= row.count) xp += row.xp;
      });
      return xp;
    },

    vehicleScore: function (parts, equippedKeys) {
      var cfg = this.config.score;
      var points = 0;
      var i;
      for (i = 0; i < (parts || []).length; i++) {
        var part = parts[i];
        var verified = this.isVerified(part);
        var equipped = this.isEquipped(part, equippedKeys);
        var partPts = 0;
        if (verified) {
          partPts += cfg.verifiedPart;
          if (equipped) partPts += cfg.equippedVerifiedPart;
        } else {
          partPts += cfg.verifiedPart * cfg.ownerReportedFactor;
          if (equipped) partPts += cfg.equippedVerifiedPart * cfg.ownerReportedFactor;
        }
        points += partPts;
      }
      var counts = this.tallyCategories(parts, equippedKeys);
      var id;
      for (id in counts) {
        if (!Object.prototype.hasOwnProperty.call(counts, id)) continue;
        if (counts[id].equipped >= 1) points += cfg.categoryCovered;
        if (counts[id].equipped >= cfg.categoryDeepAt) points += cfg.categoryDeep;
      }
      return Math.round(points);
    },

    driverXp: function (allParts, groups, equippedKeys) {
      var cfg = this.config.xp;
      var parts = allParts || [];
      var xp = 0;
      var verified = [];
      var i;
      for (i = 0; i < parts.length; i++) {
        if (!this.isVerified(parts[i])) continue;
        verified.push(parts[i]);
        xp += this.spend(parts[i]) * cfg.dollarsPerXp;
        if (this.isEquipped(parts[i], equippedKeys)) xp += cfg.equipBonus;
      }
      xp += this.purchaseMilestoneXp(verified.length);
      var self = this;
      (groups || []).forEach(function (group) {
        if (group && group.unassigned) return;
        xp += self.categoryMilestoneXp(group.parts || [], equippedKeys);
      });
      return Math.max(0, Math.round(xp));
    },

    upgrades: function (parts, equippedKeys) {
      var rows = [];
      var i;
      for (i = 0; i < (parts || []).length; i++) {
        var part = parts[i];
        var verified = this.isVerified(part);
        var categoryId = this.categorize(part);
        var url = this.catalogProductUrl(part);
        rows.push({
          name: part.name || part.title || "Part",
          brand: part.brand || part.vendor || "",
          categoryId: categoryId,
          category: this.categoryLabel(categoryId),
          spend: this.spend(part),
          price: this.money(this.spend(part)),
          verified: verified,
          ownerReported: !verified,
          equipped: this.isEquipped(part, equippedKeys),
          url: this.isValidProductUrl(url) ? url : "",
          recency: this.partRecency(part),
          index: i
        });
      }
      return rows;
    },

    upgradeStatus: function (row) {
      if (row && row.equipped) {
        return { key: "installed", label: "OWNER EQUIPPED", verified: Boolean(row.verified) };
      }
      if (row && row.verified) {
        return { key: "owned", label: "VERIFIED PURCHASE", verified: true };
      }
      return { key: "owned", label: "OWNED", verified: false };
    },

    splitUpgradeName: function (row) {
      var name = String((row && row.name) || "Part");
      var brand = String((row && row.brand) || "").trim();
      var title = name;
      if (brand) {
        var escaped = brand.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        title = name.replace(new RegExp("^" + escaped + "\\s*[-–:]?\\s*", "i"), "").trim() || name;
      }
      return { brand: brand, title: title };
    },

    classTechName: function (letter, fallback) {
      var map = {
        D: "STOCK BUILD",
        C: "MODIFIED",
        B: "STREET PERFORMANCE",
        A: "ADVANCED PERFORMANCE",
        S: "ELITE BUILD"
      };
      var key = String(letter || "D").toUpperCase();
      return map[key] || fallback || "STOCK BUILD";
    },

    serializeComponents: function (model) {
      var self = this;
      return (model && model.upgrades ? model.upgrades : []).map(function (row) {
        var status = self.upgradeStatus(row);
        var names = self.splitUpgradeName(row);
        return {
          name: names.title,
          brand: names.brand,
          category: row.category || "",
          verified: Boolean(row.verified),
          equipped: Boolean(row.equipped),
          status: status.label,
          price: row.spend > 0 ? row.price : ""
        };
      });
    },

    partRecency: function (part) {
      if (!part) return 0;
      var raw = part.createdAt || part.purchasedAt || part.processedAt || part.orderCreatedAt || part.addedAt;
      var n = Date.parse(raw || "");
      return Number.isFinite(n) ? n : 0;
    },

    compactUpgrades: function (upgrades, limit) {
      var cap = Math.max(1, Number(limit) || 4);
      var rows = (upgrades || []).filter(function (row) {
        return row && row.verified;
      });
      rows.sort(function (a, b) {
        var ae = a.equipped ? 1 : 0;
        var be = b.equipped ? 1 : 0;
        if (be !== ae) return be - ae;
        var ar = Number(a.recency) || 0;
        var br = Number(b.recency) || 0;
        if (br !== ar) return br - ar;
        return (a.index || 0) - (b.index || 0);
      });
      return {
        total: rows.length,
        shown: rows.slice(0, cap),
        more: Math.max(0, rows.length - cap)
      };
    },

    shareText: function (model) {
      var lines = [
        "My Brace1 Build",
        model.vehicleLabel,
        model.classLetter + " Class — " + this.classTechName(model.classLetter, model.className),
        "LV. " + model.level + " " + model.title,
        model.equipped + " installed / " + model.verified + " verified",
        model.value + " build value"
      ];
      (model.upgrades || []).slice(0, 8).forEach(function (row) {
        var status = BUILD_CARD.upgradeStatus(row);
        var names = BUILD_CARD.splitUpgradeName(row);
        lines.push((names.brand ? names.brand + " " : "") + names.title + " — " + status.label);
      });
      return lines.join("\n");
    },

    profileUrl: function (vehicle) {
      var cached = readCachedShareUrl(vehicle);
      var key = vehicleCacheKey(vehicle);
      var list = root.__BRACE1_PUBLIC_BUILDS || [];
      var api = root.BRACE1_GARAGE_API;
      if ((!list || !list.length) && api && typeof api.publicBuilds === "function") {
        list = api.publicBuilds() || [];
      }
      var i;
      for (i = 0; i < list.length; i++) {
        var row = list[i] || {};
        if (String(row.vehicleKey || "") === key && isPublicShareUrl(row.publicUrl)) {
          writeCachedShareUrl(vehicle, row.publicUrl);
          return row.publicUrl;
        }
      }
      return cached;
    },

    shareSheetTitle: function (model) {
      var label = [model && model.year, model && model.make, model && model.model]
        .filter(Boolean)
        .join(" ");
      return label ? label + " — Brace1 Build" : "Brace1 Build";
    },

    ownedParts: function (parts) {
      var self = this;
      return (parts || []).filter(function (part) {
        return self.isVerified(part) || self.isOwnerReported(part);
      });
    },

    classRank: function (letter) {
      var order = { D: 0, C: 1, B: 2, A: 3, S: 4 };
      var key = String(letter || "D").toUpperCase();
      return Object.prototype.hasOwnProperty.call(order, key) ? order[key] : 0;
    },

    classThreshold: function (letter) {
      var rows = this.config.score.classes;
      var i;
      for (i = 0; i < rows.length; i++) {
        if (rows[i].letter === letter) return rows[i].min;
      }
      return 0;
    },

    rarityWeight: function (rarity) {
      return this.config.rarity[rarity] || 0;
    },

    vehicleSnapshot: function (parts, equippedKeys) {
      var owned = this.ownedParts(parts);
      var verified = owned.filter(this.isVerified.bind(this));
      var equipped = owned.filter(function (part) {
        return BUILD_CARD.isEquipped(part, equippedKeys);
      });
      var score = this.vehicleScore(owned, equippedKeys);
      var klass = this.pickBand(this.config.score.classes, score);
      return {
        verified: verified.length,
        equipped: equipped.length,
        score: score,
        classLetter: klass.letter,
        className: klass.name,
        cats: this.tallyCategories(owned, equippedKeys)
      };
    },

    driverSnapshot: function (allParts, groups, equippedKeys) {
      var owned = this.ownedParts(allParts);
      var verified = owned.filter(this.isVerified.bind(this));
      var snapshots = [];
      var i;
      for (i = 0; i < (groups || []).length; i++) {
        if (groups[i] && groups[i].unassigned) continue;
        snapshots.push(this.vehicleSnapshot(groups[i].parts || [], equippedKeys));
      }
      var vehicleCount = 0;
      var bestClass = "D";
      var bestScore = 0;
      for (i = 0; i < snapshots.length; i++) {
        if (snapshots[i].verified > 0) vehicleCount += 1;
        if (this.classRank(snapshots[i].classLetter) > this.classRank(bestClass)) {
          bestClass = snapshots[i].classLetter;
        }
        if (snapshots[i].score > bestScore) bestScore = snapshots[i].score;
      }
      return {
        verified: verified.length,
        vehicleCount: vehicleCount,
        bestClass: bestClass,
        bestScore: bestScore,
        snapshots: snapshots
      };
    },

    achievementProgress: function (def, stats) {
      var req = def.requirement || {};
      var current = 0;
      var target = Number(req.count) || 1;
      var label = "";
      var score = 0;
      var targetScore = 0;
      var cat;
      if (req.kind === "vehicleVerified") {
        current = stats.vehicle.verified;
        label = current + " / " + target + " verified parts";
      } else if (req.kind === "vehicleEquippedCat") {
        cat = req.category || "";
        current = (stats.vehicle.cats[cat] && stats.vehicle.cats[cat].equipped) || 0;
        label = current + " / " + target + " " + this.categoryLabel(cat).toLowerCase() + " upgrades";
      } else if (req.kind === "vehicleClass") {
        current = this.classRank(stats.vehicle.classLetter);
        target = this.classRank(req.letter);
        score = stats.vehicle.score;
        targetScore = this.classThreshold(req.letter);
        label = stats.vehicle.classLetter + " CLASS · " + score + " / " + targetScore + " Build Score";
      } else if (req.kind === "driverVerified") {
        current = stats.driver.verified;
        label = current + " / " + target + " verified parts";
      } else if (req.kind === "driverVehicles") {
        current = stats.driver.vehicleCount;
        label = current + " / " + target + " vehicles";
      } else if (req.kind === "driverClass") {
        current = this.classRank(stats.driver.bestClass);
        target = this.classRank(req.letter);
        score = stats.driver.bestScore;
        targetScore = this.classThreshold(req.letter);
        label = stats.driver.bestClass + " CLASS · " + score + " / " + targetScore + " Build Score";
      }
      var earned = current >= target;
      var pct = target ? Math.max(0, Math.min(100, (current / target) * 100)) : 0;
      if (req.kind === "vehicleClass" || req.kind === "driverClass") {
        pct = targetScore ? Math.max(0, Math.min(100, (score / targetScore) * 100)) : 0;
      }
      return {
        current: current,
        target: target,
        label: label,
        pct: pct,
        score: score,
        targetScore: targetScore,
        earned: earned
      };
    },

    evaluateAchievement: function (def, stats) {
      var progress = this.achievementProgress(def, stats);
      return {
        id: def.id,
        name: def.name,
        description: def.description,
        type: def.type,
        icon: def.icon,
        rarity: def.rarity,
        xpReward: def.xpReward || 0,
        priority: def.priority || 0,
        requirement: def.requirement,
        earned: progress.earned,
        progress: progress,
        earnedAt: null
      };
    },

    getAchievements: function (stats) {
      /* stats.completion is exposed for future badges. Current 19 defs do not read it. */
      var defs = this.config.achievements || [];
      var rows = [];
      var i;
      for (i = 0; i < defs.length; i++) {
        rows.push(this.evaluateAchievement(defs[i], stats));
      }
      rows.sort(function (a, b) {
        if (a.earned !== b.earned) return a.earned ? -1 : 1;
        if (b.priority !== a.priority) return b.priority - a.priority;
        return a.name.localeCompare(b.name);
      });
      return rows;
    },

    getEarnedAchievements: function (rows) {
      return (rows || []).filter(function (row) {
        return row.earned;
      });
    },

    getVehicleAchievements: function (rows) {
      return (rows || []).filter(function (row) {
        return row.type === "vehicle";
      });
    },

    getDriverAchievements: function (rows) {
      return (rows || []).filter(function (row) {
        return row.type === "driver";
      });
    },

    getFeaturedAchievements: function (rows, manualIds) {
      var earned = this.getEarnedAchievements(rows);
      var limit = this.config.featuredLimit || 3;
      var picked = [];
      var i;
      var id;
      if (manualIds && manualIds.length) {
        for (i = 0; i < manualIds.length && picked.length < limit; i++) {
          id = manualIds[i];
          earned.forEach(function (row) {
            if (row.id === id && picked.indexOf(row) === -1) picked.push(row);
          });
        }
        if (picked.length) return picked.slice(0, limit);
      }
      earned = earned.slice().sort(function (a, b) {
        var rarity = BUILD_CARD.rarityWeight(b.rarity) - BUILD_CARD.rarityWeight(a.rarity);
        if (rarity) return rarity;
        return b.priority - a.priority;
      });
      return earned.slice(0, limit);
    },

    achievementRemaining: function (row) {
      if (!row || row.earned) return 0;
      var req = row.requirement || {};
      if (req.kind === "vehicleClass" || req.kind === "driverClass") {
        return Math.max(0, (row.progress.targetScore || 0) - (row.progress.score || 0));
      }
      return Math.max(0, (row.progress.target || 0) - (row.progress.current || 0));
    },

    milestoneHint: function (row) {
      var left = this.achievementRemaining(row);
      var req = (row && row.requirement) || {};
      if (req.kind === "vehicleClass" || req.kind === "driverClass") {
        return left + " POINT" + (left === 1 ? "" : "S") + " TO GO";
      }
      if (req.kind === "vehicleEquippedCat") {
        return left + " MORE " + this.categoryLabel(req.category) + " UPGRADE" + (left === 1 ? "" : "S");
      }
      if (req.kind === "driverVehicles") {
        return left + " MORE VEHICLE" + (left === 1 ? "" : "S");
      }
      return left + " MORE VERIFIED PART" + (left === 1 ? "" : "S");
    },

    getNearestMilestone: function (rows) {
      var locked = (rows || []).filter(function (row) {
        return !row.earned;
      });
      if (!locked.length) return null;
      var inProgress = locked.filter(function (row) {
        return row.progress && row.progress.current > 0;
      });
      var pool = inProgress.length ? inProgress : locked;
      var self = this;
      pool.sort(function (a, b) {
        var ra = self.achievementRemaining(a);
        var rb = self.achievementRemaining(b);
        if (ra !== rb) return ra - rb;
        return b.priority - a.priority;
      });
      var row = pool[0];
      return {
        id: row.id,
        name: row.name,
        description: row.description,
        type: row.type,
        progress: row.progress,
        hint: this.milestoneHint(row),
        requirement: row.requirement
      };
    },

    achievementXp: function (rows) {
      var seen = {};
      var xp = 0;
      var i;
      var row;
      for (i = 0; i < (rows || []).length; i++) {
        row = rows[i];
        if (!row || !row.earned || seen[row.id]) continue;
        seen[row.id] = true;
        xp += Number(row.xpReward) || 0;
      }
      return xp;
    },

    badgeIcon: function (key) {
      var icons = {
        wrench: '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M14 4l6 6-3 1-5 5-3-3 5-5 1-4Z" fill="none" stroke="currentColor" stroke-width="1.7"/><path d="M4 20l6-6" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>',
        suspension: '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M6 4v16M18 4v16M6 12h12M9 7v10M15 7v10" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>',
        brake: '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><circle cx="12" cy="12" r="7" fill="none" stroke="currentColor" stroke-width="1.7"/><circle cx="12" cy="12" r="2.2" fill="currentColor"/></svg>',
        engine: '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M4 10h3l2-3h6l2 3h3v7H4v-7Z" fill="none" stroke="currentColor" stroke-width="1.7"/><path d="M9 14h6" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>',
        trophy: '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M8 5h8v4a4 4 0 0 1-8 0V5Z" fill="none" stroke="currentColor" stroke-width="1.7"/><path d="M8 7H5v2a3 3 0 0 0 3 3M16 7h3v2a3 3 0 0 1-3 3M12 13v3M9 20h6" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>',
        garage: '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M4 11l8-6 8 6v9H4v-9Z" fill="none" stroke="currentColor" stroke-width="1.7"/><path d="M9 20v-6h6v6" stroke="currentColor" stroke-width="1.7"/></svg>',
        star: '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M12 4l2.2 4.6L19 9.3l-3.5 3.4.8 4.8L12 15.4 7.7 17.5l.8-4.8L5 9.3l4.8-.7L12 4Z" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/></svg>',
        shield: '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6l8-3Z" fill="none" stroke="currentColor" stroke-width="1.7"/></svg>',
        speed: '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M4 16a8 8 0 1 1 16 0" fill="none" stroke="currentColor" stroke-width="1.7"/><path d="M12 16l4-6" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>',
        crown: '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M4 16l2-9 6 5 6-5 2 9H4Z" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/></svg>'
      };
      return icons[key] || icons.star;
    },

    showUnlock: function (host, achievement) {
      if (!host || !achievement) return;
      host.hidden = false;
      host.classList.add("is-on");
      var name = host.querySelector("[data-ba-bc-unlock-name]");
      var xp = host.querySelector("[data-ba-bc-unlock-xp]");
      if (name) name.textContent = achievement.name;
      if (xp) xp.textContent = "+" + achievement.xpReward + " XP";
      clearTimeout(host._baUnlockTimer);
      host._baUnlockTimer = setTimeout(function () {
        host.classList.remove("is-on");
        host.hidden = true;
      }, 3200);
    },

    notifyNewUnlocks: function (host, previousIds, rows) {
      if (!host || !previousIds || !previousIds.length) return;
      var seen = {};
      previousIds.forEach(function (id) {
        seen[id] = true;
      });
      var fresh = this.getEarnedAchievements(rows).filter(function (row) {
        return !seen[row.id];
      });
      if (fresh[0]) this.showUnlock(host, fresh[0]);
    },

    compute: function (input) {
      input = input || {};
      var allParts = this.ownedParts(input.allParts || []);
      var vehicle = input.vehicle && input.vehicle.make ? input.vehicle : null;
      var vehicleParts = this.ownedParts(input.vehicleParts || []);
      var equippedKeys = input.equippedKeys || [];
      var groups = input.groups || [];
      var vehicleSnap = this.vehicleSnapshot(vehicleParts, equippedKeys);
      var driverSnap = this.driverSnapshot(allParts, groups, equippedKeys);
      if (!driverSnap.snapshots.length && vehicle) {
        driverSnap.snapshots = [vehicleSnap];
        driverSnap.vehicleCount = vehicleSnap.verified > 0 ? 1 : 0;
        driverSnap.bestClass = vehicleSnap.classLetter;
        driverSnap.bestScore = vehicleSnap.score;
      }
      var garagePath = input.garagePath || "/pages/garage";
      var catalog = input.catalog || this.loadedCatalog();
      var completion = this.getBuildCompletion({
        vehicle: vehicle,
        parts: vehicleParts,
        equippedKeys: equippedKeys
      });
      var recommendation = this.getNextUpgradeRecommendation({
        vehicle: vehicle,
        parts: vehicleParts,
        equippedKeys: equippedKeys,
        catalog: catalog,
        garagePath: garagePath
      });
      var achievements = this.getAchievements({
        vehicle: vehicleSnap,
        driver: driverSnap,
        completion: completion
      });
      var badgeXp = this.achievementXp(achievements);
      var baseXp = this.driverXp(allParts, groups, equippedKeys);
      var driver = this.driverProgress(baseXp + badgeXp);
      var klass = this.pickBand(this.config.score.classes, vehicleSnap.score);
      var counts = vehicleSnap.cats;
      var verified = vehicleParts.filter(this.isVerified.bind(this));
      var equipped = vehicleParts.filter(function (part) {
        return BUILD_CARD.isEquipped(part, equippedKeys);
      });
      var value = 0;
      var i;
      for (i = 0; i < verified.length; i++) value += this.spend(verified[i]);
      var categories = this.CATEGORIES.map(function (row) {
        var n = (counts[row.id] && counts[row.id].equipped) || 0;
        return {
          id: row.id,
          label: row.label,
          level: BUILD_CARD.categoryLevel(n),
          equipped: n
        };
      });
      var empty = vehicleParts.length === 0;
      var earned = this.getEarnedAchievements(achievements);
      return {
        visibility: this.config.visibility,
        vehicle: vehicle,
        vehicleLabel: vehicle
          ? [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(" ")
          : "No vehicle selected",
        year: vehicle && vehicle.year ? String(vehicle.year) : "",
        make: vehicle && vehicle.make ? vehicle.make : "",
        model: vehicle && vehicle.model ? vehicle.model : "",
        driverName: input.driverName || "Driver",
        memberYear: input.memberYear || "",
        xpBase: baseXp,
        xpAchievements: badgeXp,
        xpTotal: driver.xp,
        level: driver.level,
        title: driver.title,
        xpLabel: driver.label,
        xpPct: driver.pct,
        classLetter: klass.letter,
        className: klass.name,
        classShort: klass.shortName || klass.name,
        score: vehicleSnap.score,
        verified: verified.length,
        equipped: equipped.length,
        value: this.money(value),
        buildId: this.displayId(input.customerId, vehicle || {}),
        categories: categories,
        upgrades: this.upgrades(vehicleParts, equippedKeys),
        empty: empty,
        shareUrl: this.profileUrl(vehicle || {}),
        garagePath: input.garagePath || "/pages/garage",
        achievements: achievements,
        achievementsEarned: earned.length,
        achievementsTotal: achievements.length,
        featured: this.getFeaturedAchievements(achievements, input.featuredIds || null),
        nextMilestone: this.getNearestMilestone(achievements),
        completion: completion,
        recommendation: recommendation
      };
    },

    paintCategoryMeters: function (node, categories, preview) {
      if (!node || !categories) return;
      var effects = {};
      if (preview && Array.isArray(preview.effects)) {
        preview.effects.forEach(function (row) {
          if (row && row.categoryId) effects[row.categoryId] = row;
        });
      } else if (preview && preview.categoryId) {
        effects[preview.categoryId] = preview;
      }
      var compact = !node.classList.contains("ba-build-card--profile");
      var maxLevel = Number(this.config.maxCategoryLevel) || 10;
      categories.forEach(function (cat) {
        var rows = node.querySelectorAll('[data-ba-bc-cat="' + cat.id + '"], [data-ba-gt-cat="' + cat.id + '"]');
        Array.prototype.forEach.call(rows, function (row) {
          var effect = effects[cat.id];
          var showingPreview = Boolean(effect && Number(effect.delta) > 0);
          var level = showingPreview ? Number(effect.previewLevel) : cat.level;
          row.classList.toggle("is-empty", level === 0 && !showingPreview);
          row.classList.toggle("is-previewing", showingPreview || Boolean(effect && effect.maxed));
          var lv = row.querySelector("[data-ba-bc-cat-lv], [data-ba-gt-lv]");
          if (lv) {
            if (effect && effect.maxed && !showingPreview) {
              lv.textContent = compact ? "LV. " + effect.currentLevel + " MAX" : "LV. " + effect.currentLevel + " — MAX";
            } else if (showingPreview) {
              lv.textContent = compact
                ? "LV. " + effect.currentLevel + "→" + effect.previewLevel
                : "LV. " + effect.currentLevel + " → LV. " + effect.previewLevel;
            } else {
              lv.textContent = "LV. " + cat.level;
            }
          }
          var pips = Array.prototype.filter.call(row.querySelectorAll("[data-ba-bc-pips] i, [data-ba-gt-pips] i"), function (pip) {
            return window.getComputedStyle(pip).display !== "none";
          });
          var pipCount = pips.length || maxLevel;
          var pipMark = function (lv) {
            var n = Number(lv) || 0;
            if (n <= 0) return 0;
            if (n >= maxLevel) return pipCount;
            return Math.round((n / maxLevel) * pipCount);
          };
          var filled = pipMark(level);
          var currentMark = effect ? pipMark(effect.currentLevel) : 0;
          Array.prototype.forEach.call(pips, function (pip, idx) {
            var on = idx < filled;
            var isPreviewPip = showingPreview && idx >= currentMark && idx < filled;
            pip.classList.toggle("is-on", on);
            pip.classList.toggle("is-preview", isPreviewPip);
          });
          var countEl = row.querySelector("[data-ba-bc-cat-count]");
          if (countEl) {
            var n = Number(cat.equipped) || 0;
            countEl.hidden = n <= 0;
            countEl.textContent = n
              ? n + " installed component" + (n === 1 ? "" : "s")
              : "";
          }
        });
      });
    },

    applyCategoryPreview: function (payload) {
      payload = payload || {};
      if (root.document.documentElement.classList.contains("ba-has-share")) return;
      var categories = payload.categories || [];
      var preview = payload.preview || null;
      var nodes = root.document.querySelectorAll("[data-ba-build-card], [data-ba-gt-meters]");
      Array.prototype.forEach.call(nodes, function (node) {
        if (node.closest && node.closest(".is-public-share")) return;
        BUILD_CARD.paintCategoryMeters(node, categories, preview);
      });
    },

    apply: function (node, model) {
      if (!node || !model) return;
      this._lastModel = model;
      function set(sel, text) {
        var els = node.querySelectorAll(sel);
        var i;
        for (i = 0; i < els.length; i++) els[i].textContent = text;
      }
      set("[data-ba-status-name]", model.vehicleLabel);
      set("[data-ba-bc-vehicle-year]", model.year || "");
      set(
        "[data-ba-bc-vehicle-name]",
        [model.make, model.model].filter(Boolean).join(" ") || "No vehicle selected"
      );
      set("[data-ba-bc-driver]", model.driverName);
      set("[data-ba-bc-year]", model.memberYear);
      set("[data-ba-bc-level]", String(model.level));
      set("[data-ba-bc-rank]", model.title);
      set("[data-ba-bc-class-letter]", model.classLetter);
      set("[data-ba-bc-class-name]", model.className);
      set("[data-ba-bc-class-short]", model.classShort);
      set("[data-ba-bc-class-tech]", this.classTechName(model.classLetter, model.classShort));
      set("[data-ba-bc-xp]", model.xpLabel);
      set("[data-ba-bc-purchased]", String(model.verified));
      set("[data-ba-bc-equipped]", String(model.equipped));
      set("[data-ba-status-purchased]", String(model.verified));
      set("[data-ba-status-equipped]", String(model.equipped));
      set("[data-ba-bc-value]", model.value);
      set("[data-ba-bc-manifest-installed]", String(model.equipped));
      set("[data-ba-bc-manifest-verified]", String(model.verified));
      set("[data-ba-bc-manifest-value]", model.value);
      set("[data-ba-bc-score]", String(model.score));
      set("[data-ba-bc-id]", model.buildId);
      set("[data-ba-bc-year-val]", model.year || "—");
      set("[data-ba-bc-make-val]", model.make || "—");
      set("[data-ba-bc-model-val]", model.model || "—");
      set("[data-ba-bc-xp-total]", Number(model.xpTotal || 0).toLocaleString("en-US"));
      set("[data-ba-bc-ach-earned]", String(model.achievementsEarned || 0));
      set("[data-ba-bc-ach-total]", String(model.achievementsTotal || 0));
      var fills = node.querySelectorAll("[data-ba-bc-xp-fill]");
      var xpBars = node.querySelectorAll(".ba-build-card__xp-track");
      var f;
      for (f = 0; f < fills.length; f++) fills[f].style.width = model.xpPct + "%";
      for (f = 0; f < xpBars.length; f++) {
        xpBars[f].setAttribute("aria-valuenow", String(Math.round(model.xpPct || 0)));
      }
      this.paintCategoryMeters(node, model.categories, null);
      var emptyEls = node.querySelectorAll("[data-ba-bc-empty]");
      var i;
      for (i = 0; i < emptyEls.length; i++) emptyEls[i].hidden = !model.empty;
      var start = node.querySelector("[data-ba-bc-start]");
      if (start) {
        start.hidden = !model.empty;
        start.href = model.garagePath;
      }
      var list = node.querySelector("[data-ba-bc-upgrades]");
      if (list) {
        list.hidden = model.empty;
        if (model.empty) list.innerHTML = "";
        else this.renderUpgrades(list, model);
      }
      var jsonEl = node.querySelector("[data-ba-bc-components-json]");
      if (jsonEl) jsonEl.textContent = JSON.stringify(this.serializeComponents(model));
      node.setAttribute("data-ba-build-share-url", model.shareUrl || "");
      node.setAttribute("data-ba-build-share-text", this.shareText(model));
      var isPublic = isPublicShareUrl(model.shareUrl);
      var isOwner = model.visibility !== "public";
      var publicFlag = node.querySelector("[data-ba-bc-public-flag]");
      if (publicFlag) {
        if (isPublic) {
          publicFlag.hidden = false;
          publicFlag.textContent = "Public build — Active";
        } else if (isOwner) {
          publicFlag.hidden = false;
          publicFlag.textContent = "Private";
        } else {
          publicFlag.hidden = true;
        }
      }
      var caption = node.querySelector("[data-ba-bc-share-caption]");
      if (caption) {
        caption.hidden = !isPublic;
        caption.textContent = isPublic ? shortPublicUrl(model.shareUrl) : "";
      }
      var pending = node.querySelector("[data-ba-bc-qr-pending]");
      if (pending) pending.hidden = !isOwner || isPublic;
      var scan = node.querySelector("[data-ba-bc-qr-scan]");
      if (scan) scan.hidden = !isPublic;
      var qrBox = node.querySelector("[data-ba-bc-qr-box]");
      if (qrBox) {
        qrBox.hidden = isOwner && !isPublic;
        this.paintQr(qrBox, isPublic ? model.shareUrl : "");
      }
      this.applyCompletion(node, model);
      this.renderCompactUpgrades(node, model);
      this.renderRecommendation(node, model);
      this.renderFeatured(node, model);
      this.renderNextMilestone(node, model);
      this.renderAchievementGrid(node, model);
      this.bindAchievementFilters(node);
      this.bindAchievementJump(node);
    },

    applyCompletion: function (node, model) {
      if (!node || !model || !model.completion) return;
      var completion = model.completion;
      var pct = String(completion.percent);
      var copy = (this.config.completion && this.config.completion.copy) || {};
      var completeWord = (this.config.completion && this.config.completion.completeDisplay) || "COMPLETE";
      function set(sel, text) {
        var els = node.querySelectorAll(sel);
        var i;
        for (i = 0; i < els.length; i++) els[i].textContent = text;
      }
      set("[data-ba-bc-complete-pct]", pct);
      set("[data-ba-bc-complete-label]", completion.label);
      set(
        "[data-ba-bc-complete-copy]",
        this.formatCopy(copy.developed || "Your build is {pct}% developed", { pct: pct })
      );
      var fills = node.querySelectorAll("[data-ba-bc-complete-fill]");
      var bars = node.querySelectorAll("[data-ba-bc-complete-bar]");
      var i;
      for (i = 0; i < fills.length; i++) fills[i].style.width = completion.percent + "%";
      for (i = 0; i < bars.length; i++) {
        bars[i].setAttribute("aria-valuenow", pct);
        bars[i].setAttribute("aria-label", "Build completion " + pct + " percent");
      }
      node.setAttribute("data-ba-complete-pct", pct);
      node.setAttribute("data-ba-complete-label", completion.label);
      (completion.categories || []).forEach(function (cat) {
        var rows = node.querySelectorAll('[data-ba-bc-complete-cat="' + cat.id + '"]');
        var r;
        for (r = 0; r < rows.length; r++) {
          var row = rows[r];
          row.classList.toggle("is-empty", cat.percent === 0);
          row.classList.toggle("is-complete", cat.complete);
          row.setAttribute("data-percent", String(cat.percent));
          row.setAttribute(
            "aria-label",
            cat.label.replace(/&/g, "and") + " build completion " + cat.percent + " percent"
          );
          var fill = row.querySelector("[data-ba-bc-complete-cat-fill]");
          if (fill) fill.style.width = cat.percent + "%";
          var value = row.querySelector("[data-ba-bc-complete-cat-pct]");
          if (value) value.textContent = cat.complete ? completeWord : cat.percent + "%";
          var bar = row.querySelector("[data-ba-bc-complete-cat-bar]");
          if (bar) {
            bar.setAttribute("aria-valuenow", String(cat.percent));
            bar.setAttribute(
              "aria-label",
              cat.label.replace(/&/g, "and") + " build completion " + cat.percent + " percent"
            );
          }
        }
      });
      this.renderLeadCategories(node, completion);
    },

    renderLeadCategories: function (node, completion) {
      var host = node.querySelector("[data-ba-bc-complete-lead]");
      if (!host) return;
      var completeWord = (this.config.completion && this.config.completion.completeDisplay) || "COMPLETE";
      var ranked = (completion.categories || [])
        .filter(function (cat) {
          return cat.percent > 0;
        })
        .slice()
        .sort(function (a, b) {
          if (b.percent !== a.percent) return b.percent - a.percent;
          return BUILD_CARD.recommendRank(a.id) - BUILD_CARD.recommendRank(b.id);
        })
        .slice(0, 3);
      host.innerHTML = "";
      host.hidden = ranked.length === 0;
      ranked.forEach(function (cat) {
        var item = document.createElement("li");
        item.setAttribute("data-ba-bc-complete-cat", cat.id);
        item.setAttribute(
          "aria-label",
          cat.label.replace(/&/g, "and") + " build completion " + cat.percent + " percent"
        );
        var name = document.createElement("span");
        name.className = "ba-build-card__lead-name";
        name.textContent = cat.label;
        var value = document.createElement("strong");
        value.className = "ba-build-card__lead-pct";
        value.setAttribute("data-ba-bc-complete-cat-pct", "");
        value.textContent = cat.complete ? completeWord : cat.percent + "%";
        var track = document.createElement("span");
        track.className = "ba-build-card__lead-track";
        track.setAttribute("role", "progressbar");
        track.setAttribute("aria-valuemin", "0");
        track.setAttribute("aria-valuemax", "100");
        track.setAttribute("aria-valuenow", String(cat.percent));
        track.setAttribute(
          "aria-label",
          cat.label.replace(/&/g, "and") + " build completion " + cat.percent + " percent"
        );
        var fill = document.createElement("i");
        fill.setAttribute("data-ba-bc-complete-cat-fill", "");
        fill.style.width = cat.percent + "%";
        track.appendChild(fill);
        item.appendChild(name);
        item.appendChild(value);
        item.appendChild(track);
        host.appendChild(item);
      });
    },

    renderRecommendation: function (node, model) {
      var host = node.querySelector("[data-ba-bc-recommend]");
      if (!host || !model || !model.recommendation) return;
      var rec = model.recommendation;
      host.hidden = false;
      host.setAttribute("data-mode", rec.mode || "category");
      var kicker = host.querySelector("[data-ba-bc-recommend-kicker]");
      var cat = host.querySelector("[data-ba-bc-recommend-cat]");
      var name = host.querySelector("[data-ba-bc-recommend-name]");
      var hint = host.querySelector("[data-ba-bc-recommend-hint]");
      var cta = host.querySelector("[data-ba-bc-recommend-cta]");
      var secondary = host.querySelector("[data-ba-bc-recommend-secondary]");
      if (kicker) kicker.textContent = rec.kicker || "";
      if (cat) {
        cat.textContent = rec.categoryLabel || "";
        cat.hidden = !rec.categoryLabel;
      }
      if (name) {
        name.textContent = rec.name || "";
        name.hidden = !rec.name;
      }
      if (hint) hint.textContent = rec.hint || "";
      if (cta) {
        cta.textContent = rec.ctaLabel || "Explore new parts";
        cta.href = rec.ctaHref || model.garagePath || "/pages/garage";
      }
      if (secondary) {
        var showSecondary = rec.mode === "complete" && rec.secondaryHref;
        secondary.hidden = !showSecondary;
        if (showSecondary) {
          secondary.textContent = rec.secondaryLabel || "View your build";
          secondary.href = rec.secondaryHref;
        }
      }
      var levels = host.querySelector("[data-ba-bc-recommend-levels]");
      var lvNow = host.querySelector("[data-ba-bc-recommend-lv]");
      var lvTarget = host.querySelector("[data-ba-bc-recommend-target]");
      if (levels) {
        var catRow = (model.categories || []).filter(function (row) {
          return row.id === rec.categoryId;
        })[0];
        var current = catRow ? Number(catRow.level) || 0 : 0;
        var target = rec.mode === "complete" ? current : Math.min(10, current + 2);
        if (lvNow) lvNow.textContent = "LV. " + current;
        if (lvTarget) lvTarget.textContent = "LV. " + target;
        levels.hidden = rec.mode === "complete" || !rec.categoryId;
      }
    },

    renderCompactUpgrades: function (node, model) {
      var host = node.querySelector("[data-ba-bc-compact-upgrades]");
      if (!host || !model) return;
      var picked = this.compactUpgrades(model.upgrades, 4);
      var count = host.querySelector("[data-ba-bc-upgrade-count]");
      var empty = host.querySelector("[data-ba-bc-upgrade-empty]");
      var list = host.querySelector("[data-ba-bc-upgrade-list]");
      var more = host.querySelector("[data-ba-bc-upgrade-more]");
      if (count) count.textContent = String(picked.total);
      if (empty) empty.hidden = picked.total > 0;
      if (list) {
        list.innerHTML = "";
        list.hidden = picked.total === 0;
        picked.shown.forEach(function (row) {
          var item = document.createElement("li");
          var mark = document.createElement("span");
          mark.className = "ba-build-card__upgrade-mark";
          mark.setAttribute("aria-hidden", "true");
          mark.textContent = "✓";
          var name = document.createElement("span");
          name.className = "ba-build-card__upgrade-name";
          name.textContent = row.name;
          var cat = document.createElement("span");
          cat.className = "ba-build-card__upgrade-cat";
          cat.textContent = row.category || "";
          var status = row.equipped ? "Equipped verified upgrade" : "Verified upgrade";
          item.setAttribute("aria-label", status + ": " + row.name + ", " + (row.category || "part"));
          item.appendChild(mark);
          item.appendChild(name);
          item.appendChild(cat);
          list.appendChild(item);
        });
      }
      if (more) {
        more.hidden = picked.more <= 0;
        if (picked.more > 0) {
          more.textContent = "+ " + picked.more + " more upgrade" + (picked.more === 1 ? "" : "s");
        }
      }
    },

    renderUpgrades: function (host, model) {
      host.innerHTML = "";
      host.classList.remove("is-collapsed");
      var rows = (model.upgrades || []).slice();
      if (!rows.length) return;
      var order = {};
      this.CATEGORIES.forEach(function (cat, i) {
        order[cat.id] = i;
      });
      rows.sort(function (a, b) {
        var ac = Object.prototype.hasOwnProperty.call(order, a.categoryId) ? order[a.categoryId] : 99;
        var bc = Object.prototype.hasOwnProperty.call(order, b.categoryId) ? order[b.categoryId] : 99;
        if (ac !== bc) return ac - bc;
        if (Boolean(b.equipped) !== Boolean(a.equipped)) return a.equipped ? -1 : 1;
        return (Number(b.recency) || 0) - (Number(a.recency) || 0);
      });
      var groups = [];
      var lastId = "__none__";
      rows.forEach(function (row) {
        var id = row.categoryId || "";
        if (!groups.length || lastId !== id) {
          groups.push({ id: id, label: row.category || "PART", rows: [] });
          lastId = id;
        }
        groups[groups.length - 1].rows.push(row);
      });
      var table = document.createElement("div");
      table.className = "ba-build-manifest__table";
      var head = document.createElement("div");
      head.className = "ba-build-manifest__head";
      head.setAttribute("aria-hidden", "true");
      ["Part / brand", "Category", "Status", "Value"].forEach(function (label) {
        var span = document.createElement("span");
        span.textContent = label;
        head.appendChild(span);
      });
      table.appendChild(head);
      var visibleCap = 12;
      var shown = 0;
      var extra = 0;
      groups.forEach(function (group) {
        var block = document.createElement("section");
        block.className = "ba-build-manifest__group";
        var title = document.createElement("h3");
        title.className = "ba-build-manifest__group-title";
        var catRow = (model.categories || []).filter(function (cat) {
          return cat.id === group.id;
        })[0];
        var label = document.createElement("span");
        label.textContent = group.label;
        title.appendChild(label);
        if (catRow) {
          var meta = document.createElement("span");
          meta.className = "ba-build-manifest__group-meta";
          var n = Number(catRow.equipped) || 0;
          meta.textContent = "LV. " + catRow.level + " · " + n + " installed component" + (n === 1 ? "" : "s");
          title.appendChild(meta);
        }
        block.appendChild(title);
        group.rows.forEach(function (row) {
          shown += 1;
          var item = BUILD_CARD.renderUpgradeRow(row);
          if (shown > visibleCap) {
            item.classList.add("is-more");
            extra += 1;
          }
          block.appendChild(item);
        });
        table.appendChild(block);
      });
      host.appendChild(table);
      var total = document.createElement("p");
      total.className = "ba-build-manifest__total";
      var totalLabel = document.createElement("span");
      totalLabel.textContent = "Total verified build value";
      var totalValue = document.createElement("strong");
      totalValue.textContent = model.value;
      total.appendChild(totalLabel);
      total.appendChild(totalValue);
      host.appendChild(total);
      if (extra > 0) {
        host.classList.add("is-collapsed");
        var more = document.createElement("button");
        more.type = "button";
        more.className = "ba-build-manifest__more";
        more.textContent = "Show all " + rows.length + " components";
        more.addEventListener("click", function () {
          host.classList.remove("is-collapsed");
          more.hidden = true;
        });
        host.appendChild(more);
      }
    },

    renderUpgradeRow: function (row) {
      var status = this.upgradeStatus(row);
      var names = this.splitUpgradeName(row);
      var item = document.createElement("article");
      item.className = "ba-build-card-upgrade is-" + status.key + (row.verified ? " is-verified" : "");
      var part = document.createElement("div");
      part.className = "ba-build-card-upgrade__part";
      if (names.brand) {
        var brand = document.createElement("span");
        brand.className = "ba-build-card-upgrade__brand";
        brand.textContent = names.brand;
        part.appendChild(brand);
      }
      var nameEl = row.url ? document.createElement("a") : document.createElement("span");
      nameEl.className = "ba-build-card-upgrade__name";
      nameEl.textContent = names.title;
      if (row.url) nameEl.href = row.url;
      part.appendChild(nameEl);
      var cat = document.createElement("span");
      cat.className = "ba-build-card-upgrade__cat";
      cat.textContent = row.category || "";
      var pill = document.createElement("span");
      pill.className = "ba-build-card-upgrade__status is-" + status.key;
      if (status.verified) {
        var check = document.createElement("i");
        check.setAttribute("aria-hidden", "true");
        check.textContent = "✓";
        pill.appendChild(check);
      }
      var lab = document.createElement("b");
      lab.textContent = status.label;
      pill.appendChild(lab);
      var price = document.createElement("span");
      price.className = "ba-build-card-upgrade__price";
      price.textContent = row.spend > 0 ? row.price : "—";
      item.appendChild(part);
      item.appendChild(cat);
      item.appendChild(pill);
      item.appendChild(price);
      return item;
    },

    bindAchievementJump: function (node) {
      var details = node.querySelector("#achievements");
      if (!details) return;
      var links = node.querySelectorAll('a[href="#achievements"]');
      var i;
      for (i = 0; i < links.length; i++) {
        if (links[i].getAttribute("data-bound") === "1") continue;
        links[i].setAttribute("data-bound", "1");
        links[i].addEventListener("click", function () {
          details.open = true;
        });
      }
      if (root.location.hash === "#achievements") details.open = true;
    },

    renderBadge: function (row, size) {
      var el = document.createElement("span");
      el.className = "ba-build-card-badge ba-build-card-badge--" + row.rarity + (row.earned ? " is-earned" : " is-locked");
      if (size) el.className += " ba-build-card-badge--" + size;
      el.setAttribute("title", row.name);
      el.innerHTML = this.badgeIcon(row.icon);
      if (!row.earned) {
        var lock = document.createElement("i");
        lock.className = "ba-build-card-badge__lock";
        lock.setAttribute("aria-hidden", "true");
        el.appendChild(lock);
      }
      return el;
    },

    renderFeatured: function (node, model) {
      var hosts = node.querySelectorAll("[data-ba-bc-ach-featured]");
      var i;
      for (i = 0; i < hosts.length; i++) {
        hosts[i].innerHTML = "";
        (model.featured || []).forEach(function (row) {
          hosts[i].appendChild(BUILD_CARD.renderBadge(row, "sm"));
        });
      }
      var names = node.querySelector("[data-ba-bc-ach-featured-names]");
      if (names) {
        names.innerHTML = "";
        (model.featured || []).forEach(function (row) {
          var item = document.createElement("li");
          item.appendChild(BUILD_CARD.renderBadge(row, "sm"));
          var label = document.createElement("span");
          label.textContent = row.name;
          item.appendChild(label);
          names.appendChild(item);
        });
        names.hidden = !(model.featured && model.featured.length);
      }
      var featuredSection = node.querySelector(".ba-build-profile__featured");
      if (featuredSection) {
        featuredSection.hidden = !(model.featured && model.featured.length);
      }
    },

    renderNextMilestone: function (node, model) {
      var box = node.querySelector("[data-ba-bc-next]");
      if (!box) return;
      var next = model.nextMilestone;
      var name = box.querySelector("[data-ba-bc-next-name]");
      var progress = box.querySelector("[data-ba-bc-next-progress]");
      var hint = box.querySelector("[data-ba-bc-next-hint]");
      if (!next) {
        if (name) name.textContent = "All achievements unlocked";
        if (progress) progress.textContent = "";
        if (hint) hint.textContent = "";
        return;
      }
      if (name) name.textContent = next.name;
      if (progress) progress.textContent = next.progress ? next.progress.label : "";
      if (hint) hint.textContent = next.hint || "";
    },

    filteredAchievements: function (node, model) {
      var state = node.getAttribute("data-ach-state") || "all";
      var scope = node.getAttribute("data-ach-scope") || "all";
      var rows = model.achievements || [];
      if (scope === "driver") rows = this.getDriverAchievements(rows);
      if (scope === "vehicle") rows = this.getVehicleAchievements(rows);
      if (state === "earned") rows = this.getEarnedAchievements(rows);
      if (state === "locked") {
        rows = rows.filter(function (row) {
          return !row.earned;
        });
      }
      return rows;
    },

    renderAchievementGrid: function (node, model) {
      var host = node.querySelector("[data-ba-bc-ach-grid]");
      if (!host) return;
      host.innerHTML = "";
      this.filteredAchievements(node, model).forEach(function (row) {
        var card = document.createElement("article");
        card.className = "ba-build-card-ach" + (row.earned ? " is-earned" : " is-locked");
        card.setAttribute("data-rarity", row.rarity);
        var top = document.createElement("div");
        top.className = "ba-build-card-ach__top";
        top.appendChild(BUILD_CARD.renderBadge(row));
        var copy = document.createElement("div");
        var title = document.createElement("h3");
        title.textContent = row.name;
        var desc = document.createElement("p");
        desc.textContent = row.description;
        copy.appendChild(title);
        copy.appendChild(desc);
        top.appendChild(copy);
        var meta = document.createElement("p");
        meta.className = "ba-build-card-ach__meta";
        meta.textContent = row.rarity + " · +" + row.xpReward + " XP · " + row.type;
        var status = document.createElement("p");
        status.className = "ba-build-card-ach__status";
        if (row.earned) status.textContent = "Earned";
        else status.textContent = row.progress.label;
        var bar = document.createElement("span");
        bar.className = "ba-build-card-ach__bar";
        var fill = document.createElement("i");
        fill.style.width = (row.earned ? 100 : row.progress.pct) + "%";
        bar.appendChild(fill);
        card.appendChild(top);
        card.appendChild(meta);
        card.appendChild(status);
        if (!row.earned) card.appendChild(bar);
        host.appendChild(card);
      });
    },

    bindAchievementFilters: function (node) {
      var self = this;
      var buttons = node.querySelectorAll("[data-ba-bc-ach-filter]");
      if (!buttons.length || node.getAttribute("data-ach-bound") === "1") return;
      node.setAttribute("data-ach-bound", "1");
      Array.prototype.forEach.call(buttons, function (btn) {
        btn.addEventListener("click", function () {
          var group = btn.getAttribute("data-filter-group");
          var value = btn.getAttribute("data-ba-bc-ach-filter");
          if (group === "scope") node.setAttribute("data-ach-scope", value);
          else node.setAttribute("data-ach-state", value);
          Array.prototype.forEach.call(node.querySelectorAll('[data-filter-group="' + group + '"]'), function (other) {
            other.classList.toggle("is-on", other === btn);
          });
          if (self._lastModel) self.renderAchievementGrid(node, self._lastModel);
        });
      });
    },

    copyLink: function (url, onDone) {
      var done = function () {
        if (typeof onDone === "function") onDone("BUILD LINK COPIED");
      };
      if (root.navigator.clipboard && root.navigator.clipboard.writeText) {
        root.navigator.clipboard.writeText(url).then(done).catch(function () {
          fallbackCopy(url);
          done();
        });
        return;
      }
      fallbackCopy(url);
      done();
    },

    shareSheetPayload: function (model) {
      return {
        title: this.shareSheetTitle(model),
        text: "Check out my Brace1 Performance build.",
        url: model && model.shareUrl ? model.shareUrl : ""
      };
    },

    share: function (model, onDone) {
      var payload = this.shareSheetPayload(model);
      if (!payload.url) {
        if (typeof onDone === "function") onDone("UNABLE TO SHARE BUILD");
        return;
      }
      if (root.navigator.share) {
        var shareAttempt = root.navigator.share(payload);
        if (shareAttempt && typeof shareAttempt.then === "function") {
          shareAttempt.then(function () {
            if (typeof onDone === "function") onDone("BUILD LINK READY");
          }).catch(function (err) {
            if (err && err.name === "AbortError") return;
            BUILD_CARD.copyLink(payload.url, onDone);
          });
          return;
        }
      }
      this.copyLink(payload.url, onDone);
    },

    hydratePublicModel: function (published, publicUrl) {
      var model = published && typeof published === "object" ? published : {};
      var defs = this.config.achievements || [];
      var byId = {};
      defs.forEach(function (def) {
        byId[def.id] = def;
      });
      function mergeRow(row) {
        row = row || {};
        var def = byId[row.id] || {};
        return {
          id: row.id || def.id || "",
          name: row.name || row.title || def.name || "",
          description: row.description || def.description || "",
          type: row.type || def.type || "vehicle",
          icon: row.icon || def.icon || "star",
          rarity: row.rarity || def.rarity || "common",
          xpReward: Number(row.xpReward || row.xp || def.xpReward) || 0,
          priority: Number(row.priority || def.priority) || 0,
          earned: Boolean(row.earned),
          progress: row.progress || null
        };
      }
      model.achievements = (model.achievements || []).map(mergeRow);
      model.featured = (model.featured || []).map(mergeRow);
      if (!model.featured.length) {
        model.featured = this.getFeaturedAchievements(model.achievements);
      }
      model.visibility = "public";
      model.shareUrl = publicUrl || model.shareUrl || "";
      model.garagePath = model.garagePath || "/pages/garage";
      return model;
    },

    ensurePublished: function (rootEl, model) {
      var api = String((rootEl && rootEl.getAttribute("data-ba-share-api")) || "").replace(/\/$/, "");
      var ownerId = String((rootEl && rootEl.getAttribute("data-ba-customer-id")) || "").trim();
      var proof = String((rootEl && rootEl.getAttribute("data-ba-owner-proof")) || "").trim();
      var garage = root.BRACE1_GARAGE || {};
      if (!proof && garage.customer && garage.customer.proof) {
        proof = String(garage.customer.proof || "").trim();
      }
      if (!ownerId && garage.customer && garage.customer.id) {
        ownerId = String(garage.customer.id || "").trim();
      }
      if (!api && garage.shareApi) api = String(garage.shareApi || "").replace(/\/$/, "");
      if (!api) api = "https://brace1-marketplace-api.vercel.app";
      if (!api || !ownerId || !proof) {
        return Promise.reject(new Error("not_configured"));
      }
      if (!model || !model.make) {
        return Promise.reject(new Error("vehicle_required"));
      }
      return fetch(api + "/api/public/builds", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          ownerId: ownerId,
          proof: proof,
          snapshot: model
        })
      }).then(function (res) {
        return res.json().then(function (json) {
          if (!res.ok || !json || !json.publicUrl) {
            throw new Error((json && json.error) || "publish_failed");
          }
          writeCachedShareUrl(model, json.publicUrl);
          model.shareUrl = json.publicUrl;
          var existing = Array.isArray(root.__BRACE1_PUBLIC_BUILDS) ? root.__BRACE1_PUBLIC_BUILDS.slice() : [];
          var nextKey = vehicleCacheKey(model);
          var replaced = false;
          existing = existing.map(function (row) {
            if (String(row.vehicleKey || "") === nextKey) {
              replaced = true;
              return {
                vehicleKey: nextKey,
                shareToken: json.shareToken,
                publicUrl: json.publicUrl,
                buildId: model.buildId || row.buildId || ""
              };
            }
            return row;
          });
          if (!replaced) {
            existing.push({
              vehicleKey: nextKey,
              shareToken: json.shareToken,
              publicUrl: json.publicUrl,
              buildId: model.buildId || ""
            });
          }
          root.__BRACE1_PUBLIC_BUILDS = existing;
          return json;
        });
      });
    },

    refreshPublished: function (rootEl, model) {
      var api = String((rootEl && rootEl.getAttribute("data-ba-share-api")) || "").replace(/\/$/, "");
      var ownerId = String((rootEl && rootEl.getAttribute("data-ba-customer-id")) || "").trim();
      var proof = String((rootEl && rootEl.getAttribute("data-ba-owner-proof")) || "").trim();
      var garage = root.BRACE1_GARAGE || {};
      if (!proof && garage.customer && garage.customer.proof) {
        proof = String(garage.customer.proof || "").trim();
      }
      if (!ownerId && garage.customer && garage.customer.id) {
        ownerId = String(garage.customer.id || "").trim();
      }
      if (!api && garage.shareApi) api = String(garage.shareApi || "").replace(/\/$/, "");
      if (!api) api = "https://brace1-marketplace-api.vercel.app";
      if (!api || !ownerId || !proof || !model || !isPublicShareUrl(model.shareUrl)) {
        return Promise.resolve({ refreshed: false });
      }
      return fetch(api + "/api/public/builds/refresh", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          ownerId: ownerId,
          proof: proof,
          snapshot: model
        })
      }).then(function (res) {
        return res.json().then(function (json) {
          if (!res.ok) throw new Error((json && json.error) || "refresh_failed");
          if (json && json.publicUrl) {
            writeCachedShareUrl(model, json.publicUrl);
            model.shareUrl = json.publicUrl;
          }
          return json;
        });
      }).catch(function (err) {
        console.warn("Brace1 public build refresh failed", err);
        return { refreshed: false };
      });
    },

    paintQr: function (host, url) {
      if (!host) return Promise.resolve("");
      var payload = isPublicShareUrl(url) ? url : "";
      if (!payload) {
        host.innerHTML = "";
        host.removeAttribute("data-ba-qr-payload");
        return Promise.resolve("");
      }
      function showFallback(reason) {
        console.warn("Brace1 QR render failed", reason || "");
        host.innerHTML = "";
        var fallback = document.createElement("p");
        fallback.className = "ba-build-card-qr__fallback";
        fallback.textContent = shortPublicUrl(payload);
        host.appendChild(fallback);
        host.setAttribute("data-ba-qr-payload", payload);
        return payload;
      }
      function draw() {
        if (root.BRACE1_QR && typeof root.BRACE1_QR.render === "function") {
          return root.BRACE1_QR.render(host, payload, 168).catch(function (err) {
            return showFallback(err);
          });
        }
        return null;
      }
      var painted = draw();
      if (painted) return painted;
      return new Promise(function (resolve) {
        var tries = 0;
        var timer = root.setInterval(function () {
          tries += 1;
          var retry = draw();
          if (retry) {
            root.clearInterval(timer);
            retry.then(resolve, function (err) {
              resolve(showFallback(err));
            });
            return;
          }
          if (tries >= 20) {
            root.clearInterval(timer);
            resolve(showFallback("qr library missing"));
          }
        }, 50);
      });
    }
  };

  function fallbackCopy(text) {
    try {
      var ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    } catch (e) {}
  }

  function currentModel(rootEl) {
    return BUILD_CARD._lastModel || {
      shareUrl: rootEl.getAttribute("data-ba-build-share-url") || ""
    };
  }

  function publishThen(rootEl, after) {
    var model = currentModel(rootEl);
    var shareBtn = rootEl.querySelector("[data-ba-bc-share]");
    var copyBtn = rootEl.querySelector("[data-ba-bc-copy]");
    if (shareBtn) shareBtn.disabled = true;
    if (copyBtn) copyBtn.disabled = true;
    BUILD_CARD.ensurePublished(rootEl, model)
      .then(function (json) {
        model.shareUrl = json.publicUrl;
        BUILD_CARD.apply(rootEl, model);
        after(model);
      })
      .catch(function (err) {
        var code = err && err.message;
        var msg = "UNABLE TO SHARE BUILD";
        if (code === "not_configured") msg = "BUILD SHARING IS NOT CONFIGURED";
        else if (code === "vehicle_required") msg = "ADD A VEHICLE TO SHARE";
        announce(rootEl, msg);
        var pending = rootEl.querySelector("[data-ba-bc-qr-pending]");
        if (pending) {
          pending.hidden = false;
          pending.textContent = msg;
        }
      })
      .then(function () {
        if (shareBtn) shareBtn.disabled = false;
        if (copyBtn) copyBtn.disabled = false;
      });
  }

  function bindShare(rootEl) {
    var btn = rootEl.querySelector("[data-ba-bc-share]");
    if (btn && btn.getAttribute("data-bound") !== "1") {
      btn.setAttribute("data-bound", "1");
      btn.addEventListener("click", function () {
        publishThen(rootEl, function (model) {
          BUILD_CARD.share(model, function (msg) {
            announce(rootEl, msg);
          });
        });
      });
    }
    var copyBtn = rootEl.querySelector("[data-ba-bc-copy]");
    if (copyBtn && copyBtn.getAttribute("data-bound") !== "1") {
      copyBtn.setAttribute("data-bound", "1");
      copyBtn.addEventListener("click", function () {
        publishThen(rootEl, function (model) {
          BUILD_CARD.copyLink(model.shareUrl, function (msg) {
            announce(rootEl, msg);
          });
        });
      });
    }
  }

  function announce(rootEl, msg) {
    var live =
      (rootEl && rootEl.querySelector("[data-ba-bc-toast]")) ||
      document.querySelector("[data-ba-bc-toast]") ||
      document.getElementById("toast");
    if (!live) return;
    live.textContent = msg;
    live.classList.add("is-on");
    live.hidden = false;
    setTimeout(function () {
      live.classList.remove("is-on");
    }, 3200);
  }

  function garageApi() {
    return root.BRACE1_GARAGE_API || null;
  }

  function wantShareFull() {
    var full = String(new URLSearchParams(root.location.search).get("full") || "").trim();
    return full !== "" && full !== "0";
  }

  function shareSurfaceUrls(token) {
    var params = new URLSearchParams(root.location.search);
    params.set("share", token);
    params.delete("full");
    var face = root.location.pathname + "?" + params.toString();
    params.set("full", "1");
    return { face: face, full: root.location.pathname + "?" + params.toString() };
  }

  function applyShareSurface(rootEl, token) {
    var isFull = wantShareFull();
    var urls = shareSurfaceUrls(token);
    if (isFull) root.document.documentElement.classList.add("ba-share-full");
    else root.document.documentElement.classList.remove("ba-share-full");
    var face = rootEl.querySelector("[data-ba-share-face]");
    var fullEl = rootEl.querySelector("[data-ba-share-full]");
    if (face) {
      face.hidden = isFull;
      var faceCard = face.querySelector("[data-ba-build-card]");
      if (faceCard) faceCard.hidden = false;
      var cta = face.querySelector("[data-ba-status-cta]");
      if (cta) cta.setAttribute("href", urls.full);
    }
    if (fullEl) fullEl.hidden = !isFull;
    var back = rootEl.querySelector("[data-ba-share-face-link]");
    if (back) back.setAttribute("href", urls.face);
  }

  function showMissing(rootEl) {
    root.document.documentElement.classList.add("ba-has-share", "ba-share-missing");
    rootEl.classList.add("is-share-missing");
    var hideEls = rootEl.querySelectorAll("[data-ba-build-card], [data-ba-share-face]");
    var i;
    for (i = 0; i < hideEls.length; i++) hideEls[i].hidden = true;
    var missing = rootEl.querySelector("[data-ba-share-missing]");
    if (missing) missing.hidden = false;
  }

  function bootPublic(rootEl, token) {
    var api = String(rootEl.getAttribute("data-ba-share-api") || "").replace(/\/$/, "");
    if (!api) api = "https://brace1-marketplace-api.vercel.app";
    if (!api || !SHARE_TOKEN_RE.test(token)) {
      showMissing(rootEl);
      return;
    }
    fetch(api + "/api/public/builds/" + encodeURIComponent(token))
      .then(function (res) {
        return res.json().then(function (json) {
          if (!res.ok || !json || !json.published) throw new Error("not_found");
          return json;
        });
      })
      .then(function (json) {
        var model = BUILD_CARD.hydratePublicModel(json.published, json.publicUrl);
        model.garagePath = rootEl.getAttribute("data-garage-path") || "/pages/garage";
        BUILD_CARD.apply(rootEl, model);
      })
      .catch(function () {
        showMissing(rootEl);
      });
  }

  function bootFromGarage(rootEl) {
    if ((rootEl.getAttribute("data-ba-build-visibility") || BUILD_CARD.config.visibility) !== "owner") {
      return null;
    }
    var api = garageApi();
    var parts = api && typeof api.garageParts === "function" ? api.garageParts() : [];
    var groups = api && typeof api.vehicleGroups === "function" ? api.vehicleGroups() : [];
    var equipped = api && typeof api.equippedKeys === "function" ? api.equippedKeys() : [];
    var last = api && typeof api.lastVehicle === "function" ? api.lastVehicle() : null;
    var params = new URLSearchParams(root.location.search);
    if (params.get("make") && params.get("model")) {
      last = {
        year: params.get("year") || (last && last.year),
        make: params.get("make"),
        model: params.get("model")
      };
    }
    var vehicleParts = [];
    if (last && last.make) {
      vehicleParts = parts.filter(function (part) {
        return BUILD_CARD.fitsVehicle(part, last);
      });
    }
    var model = BUILD_CARD.compute({
      allParts: parts,
      groups: groups,
      vehicleParts: vehicleParts,
      vehicle: last,
      equippedKeys: equipped,
      driverName: rootEl.getAttribute("data-ba-driver-name") || "Driver",
      memberYear: rootEl.getAttribute("data-ba-member-year") || "",
      customerId: rootEl.getAttribute("data-ba-customer-id") || "",
      garagePath: rootEl.getAttribute("data-garage-path") || "/pages/garage"
    });
    BUILD_CARD.apply(rootEl, model);
    bindShare(rootEl);
    if (isPublicShareUrl(model.shareUrl)) {
      BUILD_CARD.refreshPublished(rootEl, model);
    }
    return model;
  }

  function waitForGarage(rootEl) {
    var tries = 0;
    var timer = setInterval(function () {
      tries += 1;
      if (garageApi() || tries >= 24) {
        clearInterval(timer);
        bootFromGarage(rootEl);
      }
    }, 250);
    bootFromGarage(rootEl);
    root.addEventListener("brace1:garage-changed", function () {
      bootFromGarage(rootEl);
    });
  }

  function shareTokenFromPage(rootEl) {
    var fromWindow = String(root.__BRACE1_SHARE_TOKEN || "").trim().toUpperCase();
    if (fromWindow) return fromWindow;
    var fromAttr = String((rootEl && rootEl.getAttribute("data-ba-share-token")) || "").trim().toUpperCase();
    if (fromAttr) return fromAttr;
    return String(new URLSearchParams(root.location.search).get("share") || "").trim().toUpperCase();
  }

  root.BRACE1_BUILD_CARD = BUILD_CARD;

  function bindTuningPreviewChannel() {
    if (root.document.documentElement.classList.contains("ba-has-share")) return;
    try {
      var ch = new BroadcastChannel("brace1-tuning-preview");
      ch.addEventListener("message", function (ev) {
        BUILD_CARD.applyCategoryPreview(ev.data || {});
      });
    } catch (err) {}
    root.addEventListener("brace1:tuning-preview", function (ev) {
      BUILD_CARD.applyCategoryPreview((ev && ev.detail) || {});
    });
  }

  function copyText(text, done) {
    if (root.navigator.clipboard && root.navigator.clipboard.writeText) {
      root.navigator.clipboard.writeText(text).then(done).catch(function () {
        copyTextFallback(text, done);
      });
      return;
    }
    copyTextFallback(text, done);
  }

  function copyTextFallback(text, done) {
    var area = root.document.createElement("textarea");
    area.value = text;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.left = "-9999px";
    root.document.body.appendChild(area);
    area.select();
    try {
      root.document.execCommand("copy");
      done();
    } catch (err) {}
    root.document.body.removeChild(area);
  }

  function bindBuildIdCopy() {
    root.document.addEventListener("click", function (ev) {
      var btn = ev.target && ev.target.closest ? ev.target.closest("[data-ba-bc-copy-id]") : null;
      if (!btn) return;
      ev.preventDefault();
      var scope = btn.closest(".ba-build-card, .ba-build-profile__share") || root.document;
      var idEl = scope.querySelector("[data-ba-bc-id]");
      var text = idEl ? String(idEl.textContent || "").trim() : "";
      if (!text) return;
      copyText(text, function () {
        btn.classList.add("is-copied");
        btn.setAttribute("aria-label", "Build ID copied");
        root.setTimeout(function () {
          btn.classList.remove("is-copied");
          btn.setAttribute("aria-label", "Copy build ID");
        }, 1200);
      });
    });
  }

  document.addEventListener("DOMContentLoaded", function () {
    bindBuildIdCopy();
    bindTuningPreviewChannel();
    var profile = document.querySelector("[data-ba-build-profile]");
    if (!profile) return;
    var token = shareTokenFromPage(profile);
    if (token) {
      profile.classList.add("is-public-share");
      profile.setAttribute("data-ba-build-visibility", "public");
      profile.setAttribute("data-ba-share-token", token);
      profile.removeAttribute("data-brace1-garage");
      applyShareSurface(profile, token);
      if (!SHARE_TOKEN_RE.test(token) || root.document.documentElement.classList.contains("ba-share-missing")) {
        showMissing(profile);
        return;
      }
      bootPublic(profile, token);
      return;
    }
    waitForGarage(profile);
  });
})(window);
